import type { Place } from '../types/place'
import {
  arboretumWojslawiceFeaturedPhoto,
  arboretumWojslawiceGalleryPhotos,
  arboretumWojslawicePhotos,
} from './arboretumWojslawicePhotos'
import { opactwoCystersowFeaturedPhoto, opactwoCystersowPhotos } from './opactwoCystersowPhotos'
import { palacMariannyFeaturedPhoto, palacMariannyPhotos } from './palacMariannyPhotos'
import { wodospadWilczkiFeaturedPhoto, wodospadWilczkiPhotos } from './wodospadWilczkiPhotos'

const parkWBukowcuAudio = new URL('../assets/park-bukowiec-audio.wav', import.meta.url).href

const parkWBukowcuPhotos = [
  new URL('../../park-w-bukowcu/park-bukowiec-z-drona.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-bukowiec-z-drona-sniezka.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-bukowiec-zachod-sniezka.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-w-bukowcu-sniezka.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-w-bukowcu-staw-sniezka.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-w-bukowcu-widok.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-w-bukowcu-zachod-slonca.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-w-bukowcu-knajpa.webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-w-bukowcu (12 z 28).webp', import.meta.url).href,
  new URL('../../park-w-bukowcu/park-w-bukowcu (15 z 28).webp', import.meta.url).href,
]

const zalewMietkowPhotos = [new URL('../../zalew-mietkow/zalew_mietkow.JPG', import.meta.url).href]

const szwajcariaLwoweckaPhotos = [
  new URL('../../szwajcaria-lwowecka/szwajcaria-lwowecka-latem-z-przodu.webp', import.meta.url).href,
]

export const placesMini: Place[] = [
  {
    id: 1,
    name: 'Park w Bukowcu',
    category: 'Park / ogród',
    region: 'Kotlina Jeleniogórska',
    description:
      'Park w Bukowcu: romantyczny park krajobrazowy u stóp Śnieżki, którym zachwycały się całe Prusy. Stawy, widoki na Karkonosze i angielski ogród hrabiego von Redena.',
    longDescription:
      'Park w Bukowcu to jeden z najpiękniejszych romantycznych parków krajobrazowych na Dolnym Śląsku , rozległy, malowniczy i idealny na spacer z widokiem na królową Śnieżkę.',
    photos: parkWBukowcuPhotos,
    article: {
      published: true,
      intro:
        'Jakbym miała zrobić artykuł o miejscach na Dolnym Śląsku idealnych, żeby się oświadczyć, Park w Bukowcu na pewno by się na nim znalazł i zajmowałby zaszczytne pierwsze miejsce. To niesamowicie romantyczny, malowniczy oraz wyjątkowy park u podnóża samej królowej Śnieżki. Historia samego Bukowca jest absolutnie fenomenalna. Parkiem zachwycały się całe Prusy i możliwe, że i sam Caspar David Friedrich.',
      audio: {
        src: parkWBukowcuAudio,
        title: 'Posłuchaj dźwięków Parku w Bukowcu',
        description:
          'Krótka próbka ambientu z alejek i stawów, włącz nagranie, jeśli chcesz poczuć klimat miejsca jeszcze przed wyjazdem.',
      },
      sections: [
        {
          id: 'bukowiec-to',
          title: 'Bukowiec to…',
          body:
            'Porozmawiajmy o Bukowcu. To wieś w województwie dolnośląskim, w powiecie karkonoskim, położona na wysokości 400–490 m n.p.m., w Rudawach Janowickich. Nic specjalnego? A jednak to tu znajduje się pierwszy na Śląsku romantyczny park krajobrazowy, który warto znać zanim wybierzesz się w Karkonosze.',
        },
        {
          id: 'historia',
          title: 'Historia Parku w Bukowcu',
          skippable: true,
          skipLabel: 'Nie lubię historii, pomijam',
          skipScrollToId: 'wracajac-do-parku',
          body:
            'Bukowiec, a w zasadzie niemiecki Buchwald, nazwę bierze od buków występujących niegdyś na tych terenach. Nazwa miasta w pojawia się już w 1303 roku. Od tego czasu do końca XVI wieku Bukowiec był własnością Heinko von Zedlitz z Maciejowej, łowczego księcia Bolka II Świdnickiego i siedzibą rodu von Zedlitz. W 1573 roku przechodzi w posiadanie Jana Jerzego von Reibnitz, razem ze stawami rybnymi i kościołem. To on był zapewne inicjatorem budowy dworu, którego ogólny zarys zachował się po dziś dzień, lecz sam dworek nie.\n\nWłasnością tego rodu Bukowiec pozostanie przez kolejnych dwieście lat.\n\nW 1785 roku majątek kupuje <b> hrabia Fryderyk Wilhelm von Reden, dyrektor Wyższego Urzędu Górniczego w Breslau, późniejszy minister górnictwa Prus. </b> To właśnie dzięki jego staraniom powstaje Park w Bukowcu, o którym za chwilę opowiem Wam dokładniej.',
        },
        {
          id: 'park-w-bukowcu',
          title: 'Park w Bukowcu',
          body:
            'Hrabia von Reden modernizował folwark, wznosił szereg nowych budowli, takich jak browar, Dom Zarządu, stajnie, owczarnię, stodołę. Hrabia był ambitnym oraz dobrze wykształconym zarządcą, a nawet i romantykiem. W swoim życiu odbył podróż do Anglii, gdzie zapoznał się z angielskim ogrodnictwem i był nim mocno zafascynowany. Swoje marzenie o zbudowaniu romantycznego parku krajobrazowego wciela właśnie w Buchwald, razem ze swoją żoną Fryderyką von Reden.\n\nW Bukowcu wdrożył 3 typy angielskich parków krajobrazowych:\n• ogród położony przy oranżerii;\n• park obejmujący dalszą posiadłość i stapiający się z krajobrazem naturalnym (liczący 120 ha z przepięknym widokiem na Karkonosze);\n• typ ozdobnej farmy.\n\n',
              // 'Hrabia von Reden potrafił reżyserować przestrzeń, czego najlepszym dowodem jest cmentarne wzgórze. Na jego szczycie, tuż na skraju dzikiego lasu i w zagadkowym sąsiedztwie kamiennego kręgu, kazał wznieść monumentalny krucyfiks. Dziś, otoczony gęstwiną drzew, krzyż skrywa się w cieniu, lecz dawniej , o zachodzie słońca , tworzył widok mrożący krew w żyłach i zachwycający zarazem. Widok budził nieodparte skojarzenia z mistycznym arcydziełem Caspara Davida Friedricha „Krzyż w górach”. Friedrich, genialny malarz niemieckiego romantyzmu, wędrował po tych terenach i uwieczniał w swoich pracach sąsiednie Mysłakowice. Kto wie, może to właśnie Park w Bukowcu natchnął go do przelania na płótno tej przejmującej melancholii?',
        },
        {
          id: 'wracajac-do-parku',
          title: 'Wracając do parku',
          body:
            'Na przełomie XVIII i XIX wieku powstał tu pierwszy na Śląsku romantyczny park krajobrazowy. Park w Bukowcu był zgodny z angielskim kanonem romantycznego ogrodu, część projektów wyszła spod ręki samego Redena, a część przygotował uznany architekt ogrodów Hans Carl Walther.\n\nDziś Park w Bukowcu to harmonia między naturą a człowiekiem , dzieło, które trzeba zwiedzić ze względu na widoki, przyrodę oraz różnorodność. To miejsce, do którego chce się wracać: na spacer, na zdjęcia i na chwilę ciszy z panoramą Karkonoszy w tle.',
        },
        {
          id: 'co-zwiedzic',
          title: 'Co zwiedzić w Parku w Bukowcu?',
          body:
            'W Parku w Bukowcu warto zarezerwować od kilkunastu minut, nawet do kilku godzin - szczególnie, jeśli podróżują z Tobą dzieci. Na trasie czekają m.in.:\n• 10 stawów;\n• Dwór Ateny, dawna herbaciarnia hrabiny Fryderyki;\n• Dom Rybaka, czyli dzisiejsza restauracja;\n• dawne Opactwo - ruiny i punkt obserwacyjny;\n• Muzeum Tajemniczy Las;\n• zarośla wierzbowe;\n• domki dla ptactwa wodnego;\n• krąg druidów, czyli eleganckie formy skalne.\n\nWarto zatrzymać się przy wspomnianym Dworze Ateny. To neoklasyczny pawilon, dawna herbaciarnia, który hrabia Fryderyk Wilhelm von Reden podarował hrabinie z okazji drugiej rocznicy ślubu. Park w Bukowcu założyli wspólnie; hrabia tak ją kochał, że oprócz wspólnej pracy nad ogrodem chciał zostawić jej w nim coś wyjątkowego i dał jej właśnie przepiękny pawilon ukryty w zaroślach drzew.\n\nNa drugą część dnia zostaw Muzeum Tajemniczy Las, świetną atrakcję dla dzieci i dorosłych na terenie Parku w Bukowcu. To interaktywna opowieść o lesie, przyrodzie i… tym, co w nim słychać, czuć i odkrywa się krok po kroku. Dzieciaki wchodzą w rolę odkrywców, dorośli dostają coś więcej niż „wystawę pod szkłem” , klimat, zadania i narrację, która dobrze łączy się ze spacerem po parku. Jeśli jedziesz rodziną, to praktyczny sposób, żeby po romantycznych alejkach i stawach dodać do programu coś energicznego i angażującego dla wszystkich pokoleń.',
        },
        {
          id: 'jak-zaplanowac',
          title: 'Jak zaplanować wyjazd do Parku w Bukowcu?',
          body:
            'Dojazd\nZ Wrocławia samochodem: ok. 2 h. \n\nPlan zwiedzania\nWarto połączyć wizytę w Parku w Bukowcu z Rudawami Janowickimi albo z Jelenią Górą czy Karpaczem. Możliwości jest naprawdę dużo: jednego dnia park i stawy, drugiego , szlaki w okolicy.\n\nDołącz do Paszportu Dolnego Śląska, żeby zobaczyć pełną mapę miejsc i partnerów w okolicy Bukowca i Kotliny Jeleniogórskiej.',
        },
        {
          id: 'galeria',
          title: 'Galeria zdjęć: Park w Bukowcu',
          body:
            'Poniżej zdjęcia z Parku w Bukowcu: widoki z drona na Karkonosze i Śnieżkę, alejki, stawy oraz zachód słońca nad parkiem.\n\nNajlepszy klimat na zdjęcia w Parku w Bukowcu zwykle jest rano (miękkie światło) albo późnym popołudniem , wtedy park robi się bardziej „filmowy”.',
        },
      ],
      faq: [
        {
          question: 'Ile czasu warto przeznaczyć na Park w Bukowcu?',
          answer:
            'Minimum 2–3 godziny na główne alejki i stawy; bez pośpiechu spokojnie spędzisz tu pół dnia.',
        },
        {
          question: 'Czy Park w Bukowcu nadaje się na romantyczny spacer?',
          answer:
            'Tak , to jeden z najbardziej romantycznych parków krajobrazowych na Dolnym Śląsku, z widokiem na Śnieżkę i Karkonosze.',
        },
        {
          question: 'Jak dojechać do Parku w Bukowcu z Wrocławia?',
          answer: 'Najwygodniej samochodem (ok. 2 h). Alternatywnie: pociąg do Jeleniej Góry lub Karpacza i dalszy dojazd lokalny.',
        },
        {
          question: 'Co zobaczyć w Parku w Bukowcu w jeden dzień?',
          answer:
            'Stawy, Dwór Ateny, Dom Rybaka, krąg druidów, Muzeum Tajemniczy Las, punkty widokowe na Karkonosze oraz spokojne alejki między zadrzewieniami.',
        },
        {
          question: 'Czy Park w Bukowcu jest dobry dla rodzin z dziećmi?',
          answer:
            'Tak , spacer po parku jest łatwy, a Muzeum Tajemniczy Las to atrakcja dla dzieci i dorosłych , dobra na drugą część dnia po zwiedzaniu alejek i stawów.',
        },
      ],
    },
    highlights: [
      'Pierwszy romantyczny park krajobrazowy na Śląsku',
      '120 ha parku z widokiem na Karkonosze i Śnieżkę',
      '10 stawów, herbaciarnia, krąg druidów i Dom Rybaka',
    ],
    practicalInfo: {
      visitDuration: 'Najlepiej 2–4 godziny (łatwo dłużej, jeśli łączysz ze zwiedzaniem okolicy).',
      howToGetThere:
        'Park w Bukowcu: Bukowiec (gm. Mysłakowice), Rudawy Janowickie. Z Wrocławia ok. 2 h samochodem; blisko Jeleniej Góry i Karpacza.',
      parking: 'Miejsca postojowe w okolicy parku; w weekendy i w sezonie warto przyjechać wcześniej.',
      accessibility: 'Spacery po alejkach parkowych; po deszczu miejscami może być miękko i ślisko.',
      goodToKnow: [
        'Połącz wizytę w Parku w Bukowcu z Rudawami Janowickimi, Jelenią Górą lub Karpaczem.',
        'Na zdjęcia najlepiej rano lub podczas zachodu słońca.',
      ],
    },
    seo: {
      title: 'Park w Bukowcu - atrakcje, mapka, parking, zdjęcia',
      description:
        'Park w Bukowcu, którym zachwycały się całe Prusy. Zobacz, jak zaplanować wyjazd do parku - opis, mapka, historia, zdjęcia',
      keywords: [
        'Park w Bukowcu',
        'park w Bukowcu',
        'Bukowiec park',
        'romantyczny park krajobrazowy',
        'Karkonosze',
        'Śnieżka',
        'Kotlina Jeleniogórska',
        'hrabia von Reden',
        'Rudawy Janowickie',
        'Dolny Śląsk',
      ],
      image: parkWBukowcuPhotos[0],
    },
    locationLabel: 'Bukowiec (gm. Mysłakowice) / Kotlina Jeleniogórska',
    lat: 50.826,
    lng: 15.8135,
    mapPoints: [
      {
        id: 'dwor-ateny',
        name: 'Dwór Ateny (herbaciarnia)',
        lat: 50.8260294,
        lng: 15.8155925,
        description:
          'Neoklasyczny pawilon , dawna herbaciarnia hrabiny, podarowana z okazji drugiej rocznicy ślubu.',
      },
      {
        id: 'dom-rybaka',
        name: 'Dom Rybaka',
        lat: 50.8266886,
        lng: 15.8119777,
        description: 'Dawna zabudowa folwarku , dziś restauracja przy jednym ze stawów parkowych.',
      },
      {
        id: 'muzeum-tajemniczy-las',
        name: 'Muzeum Tajemniczy Las',
        lat: 50.8247449,
        lng: 15.814173,
        description: 'Interaktywna wystawa o lesie , atrakcja dla dzieci i dorosłych w zabytkowym browarze.',
      },
      {
        id: 'dawne-opactwo',
        name: 'Dawne Opactwo',
        lat: 50.8240715,
        lng: 15.8149763,
        description: 'Zabytkowy budynek w zespole pałacowo-parkowym , część dawnego założenia folwarcznego.',
      },
      {
        id: 'krag-druidow',
        name: 'Krąg druidów',
        lat: 50.8292524,
        lng: 15.8138889,
        description: 'Eleganckie formy skalne , jeden z najbardziej charakterystycznych punktów w parku.',
      },
      {
        id: 'cmentarne-wzgorze',
        name: 'Cmentarne wzgórze (krucyfiks)',
        lat: 50.82985,
        lng: 15.8126,
        description:
          'Monumentalny krucyfiks na wzgórzu , punkt widokowy kojarzony z malarską melancholią romantyzmu.',
      },
      {
        id: 'stawy',
        name: 'Stawy parkowe',
        lat: 50.8262,
        lng: 15.8132,
        description: 'Siedem głównych stawów w Parku w Bukowcu , idealne na spacer wzdłuż brzegu.',
      },
    ],
  },
  {
    id: 2,
    name: 'Opactwo Cystersów w Lubiążu',
    category: 'Zabytek',
    region: 'Lubiąż',
    description:
      'Barokowe opactwo cystersów w Lubiążu nad Odrą — jeden z największych zespołów klasztornych w Europie i obowiązkowy punkt na mapie Dolnego Śląska.',
    longDescription:
      'Opactwo Cystersów w Lubiążu to monumentalny barokowy kompleks nad Odrą — ponad 200 metrów elewacji, dziedzińce, kościół i sale, które robią wrażenie skalą. Zwiedzanie łączy architekturę z historią cystersów sięgającą XII wieku.',
    photos: opactwoCystersowPhotos,
    article: {
      published: true,
      intro:
        'Kiedy po raz pierwszy stoisz przed Opactwem Cystersów w Lubiążu, trudno ogarnąć wzrokiem całą bryłę. To nie jest „mały zamek na wycieczkę szkolną” — to jeden z największych zespołów klasztornych w Europie, rozciągający się wzdłuż Odry na ponad dwieście metrów.\n\nBarokowa fasada, ogromne dziedzińce i wnętrza pełne detali sprawiają, że Lubiąż trafia na listy „must see” Dolnego Śląska obok Wrocławia i Karkonoszy. Nawet jeśli nie jesteś fanem klasztorów, sama skala i klimat miejsca robią wrażenie — a z przewodnikiem historia cystersów dodaje kontekstu do każdej sali i kaplicy.',
      sections: [
        {
          id: 'lubiaz-to',
          title: 'Lubiąż i opactwo cystersów',
          body:
            'Lubiąż to wieś w powiecie wołowskim, w dolinie Odry, ok. 50 km na północny zachód od Wrocławia. Właśnie tu, nad rzeką, cystersi założyli w 1175 roku klasztor, który przez wieki przekształcał się w jedną z najpotężniejszych rezydencji zakonnych na Śląsku.\n\nOpactwo Cystersów w Lubiążu (dawniej Leubus) należy do największych tego typu obiektów w Europie. Barokowa przebudowa w XVII i XVIII wieku nadała mu dzisiejszy, monumentalny wygląd — długie skrzydła, wieże, bogato zdobione elewacje i rozległe dziedzińce. Z lotu ptaka widać, jak cały kompleks „przytula” brzeg Odry.',
        },
        {
          id: 'historia',
          title: 'Historia opactwa — od cystersów po współczesność',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'architektura',
          body:
            'Początki sięgają XII wieku, kiedy cystersi z siedzibą w Lubiążu stali się jednym z najważniejszych ośrodków zakonnych na Śląsku. Średniowieczny klasztor przechodził kolejne etapy rozbudowy, a jego świetność trwała przez stulecia.\n\nW XVII i XVIII wieku opactwo przebudowano w stylu barokowym — powstały reprezentacyjne sale, nowy kościół i rozległe skrzydła klasztorne. Lubiąż stał się nie tylko ośrodkiem duchowym, ale i potężnym majątkiem ziemskim.\n\nPo 1810 roku klasztor został sekularyzowany, a obiekt zmieniał funkcje: folwark, fabryka, szpital. W czasie II wojny światowej i w latach powojennych ucierpiał, m.in. jako szpital wojskowy. Od lat 80. XX wieku trwa konserwacja i odbudowa — dziś Opactwo Cystersów w Lubiążu znów można zwiedzać, a część kompleksu służy jako muzeum i centrum kultury.',
        },
        {
          id: 'architektura',
          title: 'Architektura: barok na skalę europejską',
          body:
            'To, co robi największe wrażenie w Lubiążu, to skala. Elewacja opactwa liczy ponad 200 metrów długości — jedna z najdłuższych w Europie. Barokowe fasady z wieżami, kopułami i bogatą sztukaterią sprawiają, że budynek wygląda jak pałac, a nie klasztor.\n\nWnętrza zachwycają reprezentacyjnymi salami, kaplicami i detalami epoki: stiuki, freski, drewniane okładziny i przestrzenie, które pokazują, jak cystersi (a potem ich następcy) budowali prestiż. Zwiedzanie odbywa się zwykle według ustalonej trasy — warto poświęcić na to co najmniej godzinę, a bez pośpiechu nawet dwie.\n\nZ zewnątrz warto obejść kompleks dookoła: z przodu widać monumentalną elewację, z góry lub z drona — jak opactwo wpisuje się w krajobraz doliny Odry.',
        },
        {
          id: 'co-zobaczyc',
          title: 'Co zobaczyć podczas zwiedzania',
          body:
            'Podczas wizyty w Opactwie Cystersów w Lubiążu zobaczysz m.in.:\n• reprezentacyjne sale i komnaty barokowe;\n• kościół opactwa z bogatym wystrojem;\n• dziedzińce i krużganki klasztorne;\n• detale architektury: kopuły, wieże, portale i ornamenty elewacji.\n\nTrasa zwiedzania może się różnić w zależności od sezonu i dostępności poszczególnych części obiektu — przed wyjazdem sprawdź aktualny harmonogram na stronie opactwa. Zwiedzanie z przewodnikiem to najlepszy sposób, żeby nie przeoczyć kontekstu historii i architektury.\n\nPo zwiedzaniu wnętrz zostaw chwilę na spacer wokół budynku — fasada od strony Odry i widok z drogi to osobna atrakcja, szczególnie przy miękkim świetle rankiem lub pod wieczór.',
        },
        {
          id: 'plan-wizyty',
          title: 'Jak zaplanować wizytę w Lubiążu',
          body:
            'Na Opactwo Cystersów w Lubiążu zaplanuj 1,5–2,5 godziny — zależnie od tempa i tego, czy zwiedzasz z przewodnikiem. Miłośnicy architektury i historii spokojnie zostają tu dłużej.\n\nDojazd: Lubiąż leży ok. 50 km od Wrocławia i kilka kilometrów od Wołowa. Najwygodniej samochodem (droga krajowa w stronę Brzegu lub przez Wołów). W okolicy są też połączenia autobusowe i kolejowe — stacja Lubiąż znajduje się niedaleko kompleksu.\n\nW sezonie turystycznym, zwłaszcza w weekendy, bywa tłoczno — warto przyjechać wcześnie rano albo wybrać dzień powszedni. Bilety i godziny zwiedzania sprawdź przed wyjazdem; harmonogram może się zmieniać poza sezonem lub podczas prac konserwatorskich.\n\nDobry plan dnia: rano Lubiąż, po południu Wołów, Brzeg albo trasa w stronę Legnicy. Opactwo świetnie sprawdza się jako główna atrakcja jednodniowej wycieczki z Wrocławia.',
        },
        {
          id: 'galeria',
          title: 'Galeria zdjęć: Opactwo Cystersów w Lubiążu',
          body:
            'Poniżej zdjęcia Opactwa Cystersów w Lubiążu: panorama z lotu ptaka nad Odrą, monumentalna elewacja frontowa i barokowe detale zespołu klasztornego.\n\nDo zdjęć architektury najlepiej pasuje miękkie światło rankiem albo późnym popołudniem — wtedy fasada i detale barokowe wychodzą najlepiej w kadrze.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Opactwo Cystersów w Lubiążu?',
          answer:
            'W Lubiążu (powiat wołowski, woj. dolnośląskie), nad Odrą — ok. 50 km na północny zachód od Wrocławia i kilka km od Wołowa.',
        },
        {
          question: 'Ile czasu zaplanować na zwiedzanie opactwa?',
          answer:
            'Zwykle 1,5–2,5 godziny w zależności od trasy i tempa. Bez pośpiechu, ze zwiedzaniem z przewodnikiem, spokojnie możesz tu spędzić pół dnia.',
        },
        {
          question: 'Czy da się zwiedzać opactwo w deszcz?',
          answer:
            'Tak — większość zwiedzania odbywa się we wnętrzach i pod dachem, więc Lubiąż to dobry wybór także przy gorszej pogodzie.',
        },
        {
          question: 'Czy opactwo nadaje się na wycieczkę z Wrocławia?',
          answer:
            'Tak — to jedna z najpopularniejszych jednodniowych wycieczek z Wrocławia (ok. 1 godz. jazdy samochodem). Monumentalna skala obiektu uzasadnia wyjazd nawet na kilka godzin.',
        },
        {
          question: 'Czy warto zwiedzać z przewodnikiem?',
          answer:
            'Zdecydowanie tak — przewodnik dodaje kontekst do architektury i historii cystersów, a bez niego łatwo przeoczyć znaczenie poszczególnych sal i kaplic.',
        },
        {
          question: 'Jak dojechać do Lubiąża z Wrocławia?',
          answer:
            'Najwygodniej samochodem (ok. 1 godz. przez Wołów lub w stronę Brzegu). Alternatywnie: pociąg lub autobus do Wołowa / Lubiąża i krótki dojazd do opactwa.',
        },
      ],
    },
    highlights: [
      'Jeden z największych zespołów klasztornych w Europie — ponad 200 m elewacji',
      'Barokowa architektura i reprezentacyjne wnętrza nad Odrą',
      'Obowiązkowy punkt wycieczki „po zabytkach” Dolnego Śląska',
    ],
    practicalInfo: {
      visitDuration: 'Najczęściej 1,5–2,5 godziny (łatwo dłużej ze zwiedzaniem z przewodnikiem).',
      howToGetThere:
        'Lubiąż, pow. wołowski — ok. 50 km od Wrocławia; najwygodniej samochodem, w okolicy stacja kolejowa Lubiąż.',
      parking: 'Parking w okolicy obiektu; w sezonie i w weekendy warto przyjechać wcześniej.',
      accessibility: 'Duży obiekt — sporo chodzenia; miejscami schody, progi i nierówne nawierzchnie.',
      goodToKnow: [
        'Połącz wizytę z Wołowem, Brzegiem lub trasą w stronę Legnicy.',
        'Godziny zwiedzania i cennik sprawdź przed wyjazdem — mogą się różnić w zależności od sezonu.',
        'Zwiedzanie z przewodnikiem znacząco wzbogaca wizytę — warto zarezerwować termin z wyprzedzeniem.',
      ],
    },
    seo: {
      title: 'Opactwo Cystersów w Lubiążu | zwiedzanie, bilety, zdjęcia, dojazd z Wrocławia',
      description:
        'Opactwo Cystersów w Lubiążu: barokowy kompleks nad Odrą, jeden z największych w Europie. Co zobaczyć, ile trwa zwiedzanie, dojazd z Wrocławia i zdjęcia z lotu ptaka.',
      keywords: [
        'Opactwo Cystersów w Lubiążu',
        'opactwo cystersów Lubiąż',
        'Lubiąż',
        'klasztor Lubiąż',
        'cystersi',
        'barok',
        'zwiedzanie Lubiąż',
        'Dolny Śląsk',
        'wycieczka z Wrocławia',
        'zabytki Dolnego Śląska',
        'Opactwo Cystersów',
        'Leubus',
      ],
      image: opactwoCystersowFeaturedPhoto?.src ?? opactwoCystersowPhotos[0]?.src,
    },
    locationLabel: 'Lubiąż',
    lat: 51.262889,
    lng: 16.468111,
  },
  {
    id: 3,
    name: 'Pałac Marianny Orańskiej',
    category: 'Pałac',
    region: 'Ziemia Kłodzka',
    description:
      'Neogotycki pałac w Kamieńcu Ząbkowickim: Marianna Orańska, tarasy, park angielski i jedna z najbardziej rozpoznawalnych rezydencji Dolnego Śląska.',
    longDescription:
      'Pałac Marianny Orańskiej w Kamieńcu Ząbkowickim to neogotycka perła Ziemi Kłodzkiej — monumentalna bryła, wnętrza z epoki i rozległe tarasy z widokiem na Góry Stołowe. Wizyta łączy zwiedzanie pałacu ze spacerem po parku krajobrazowym.',
    photos: palacMariannyPhotos,
    article: {
      published: true,
      intro:
        'Kiedy po raz pierwszy zobaczysz Pałac Marianny Orańskiej w Kamieńcu Ząbkowickim, trudno uwierzyć, że to nie scenografia filmu, tylko prawdziwa rezydencja z XIX wieku. Masywna, neogotycka bryła wyrasta z tarasów i ogrodów jak z baśni — a historia tego miejsca jest równie barwna jak jego architektura.\n\nTo jeden z najbardziej charakterystycznych pałaców na Dolnym Śląsku i punkt obowiązkowy na mapie Ziemi Kłodzkiej. Najlepiej smakuje w zestawie: wnętrza + tarasy i park, bo wtedy widać pełną skalę założenia i rozumiesz, dlaczego Kamieniec Ząbkowicki przyciąga turystów z całej Polski.',
      sections: [
        {
          id: 'kamieniec-to',
          title: 'Kamieniec Ząbkowicki i pałac Marianny Orańskiej',
          body:
            'Kamieniec Ząbkowicki to niewielka miejscowość w powiecie ząbkowickim, na pograniczu Kotliny Kłodzkiej i Gór Stołowych. Właśnie tu, na wzgórzu nad doliną, księżna Marianna Orańska — holenderska księżniczka, żona księcia Albrechta Hohenzollerna — wzniosła swoją letnią rezydencję.\n\nPałac Marianny Orańskiej powstał w latach 1838–1843 według projektu Karla Friedricha Schinkla, jednego z najwybitniejszych architektów niemieckiego neoklasycyzmu i neogotyku. Efekt? Monumentalny, średniowiecznie inspirowany pałac z wieżami, blankami i bogatą ornamentyką — jeden z najciekawszych przykładów neogotyku w Europie Środkowej.',
        },
        {
          id: 'historia',
          title: 'Historia pałacu i Marianny Orańskiej',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'architektura',
          body:
            'Marianna Orańska (1810–1883) była córką króla Holandii Willema I. Po burzliwym małżeństwie z księciem Albrechtem Pruskim osiadła na Dolnym Śląsku i stworzyła w Kamieńcu Ząbkowickim miejsce, które miało być jej własną, niezależną przestrzenią.\n\nBudowa pałacu trwała kilka lat, a po jej zakończeniu Marianna przez dekady dopracowywała założenie: park angielski, tarasy, pawilon chiński, kaplicę i liczne budowle towarzyszące. Po jej śmierci majątek przechodził w różne ręce, a w czasie II wojny światowej pałac ucierpiał — m.in. zniszczono wieżę główną.\n\nOd lat 80. XX wieku trwała konserwacja i odbudowa. Dziś Pałac Marianny Orańskiej znów można zwiedzać, a tarasy i park przyciągają nie tylko miłośników historii, ale i fotografów.',
        },
        {
          id: 'architektura',
          title: 'Architektura: neogotyk z rozmachem',
          body:
            'Pałac zachwyca formą już z daleka: masywna bryła, wieże, blanki i liczne detale neogotyckie sprawiają, że budynek wygląda jak średniowieczny zamek przeniesiony w XIX wiek. Schinkel i jego szkoła potrafili łączyć romantyczny styl z nowoczesną techniką budowlaną epoki.\n\nWnętrza zachowały klimat dawnej rezydencji: reprezentacyjne sale, komnaty z epoki i detale, które opowiadają o życiu dworu. Tempo zwiedzania zwykle narzuca trasa z przewodnikiem lub plan zwiedzania — warto poświęcić na to co najmniej godzinę.\n\nNa zewnątrz możesz chodzić we własnym rytmie: tarasy schodzą szerokimi poziomami w stronę parku, a z góry widać zarówno bryłę pałacu, jak i otaczający krajobraz Ziemi Kłodzkiej.',
        },
        {
          id: 'tarasy-i-park',
          title: 'Tarasy, park i otoczenie',
          body:
            'Tarasy pałacowe to jedna z największych atrakcji Kamieńca Ząbkowickiego. Rozległe, kamienne poziomy schodzą w dół w stronę parku krajobrazowego, tworząc efektowną perspektywę — szczególnie dobrą na zdjęcia z góry lub z lotu ptaka.\n\nPark założony w stylu angielskim otacza pałac zielenią: alejki, rzadkie gatunki drzew, punkty widokowe i spokojne zakątki na spacer. Wiosną i latem robi wrażenie bujna roślinność, jesienią — barwy liści na tle neogotyckich murów.\n\nW pobliżu warto też zwrócić uwagę na zabudowę folwarczną i pozostałości dawnego założenia pałacowego — cały kompleks tworzy spójną, malowniczą całość.',
        },
        {
          id: 'plan-wizyty',
          title: 'Jak zaplanować wizytę w pałacu',
          body:
            'Na Pałac Marianny Orańskiej zaplanuj 1,5–3 godziny — zależnie od tego, czy chcesz zobaczyć tylko z zewnątrz, czy wejść do wnętrz i przejść cały park.\n\nDojazd: Kamieniec Ząbkowicki leży ok. 60 km na południowy zachód od Wrocławia i ok. 30 km od Ząbkowic Śląskich. Najwygodniej samochodem; w okolicy są też połączenia kolejowe (stacja Kamieniec Ząbkowicki).\n\nW sezonie turystycznym (wiosna–jesień) bywa tłoczno, zwłaszcza w weekendy — warto przyjechać wcześnie rano albo wybrać dzień powszedni. Bilety i godziny zwiedzania sprawdź przed wyjazdem na stronie obiektu — harmonogram może się zmieniać w zależności od sezonu i remontów.\n\nDobry plan dnia: rano pałac i tarasy, po południu drugi punkt w okolicy — np. Ząbkowice Śląskie, Bardo Śląskie albo trasa w stronę Gór Stołowych.',
        },
        {
          id: 'galeria',
          title: 'Galeria zdjęć: Pałac Marianny Orańskiej',
          body:
            'Poniżej zdjęcia Pałacu Marianny Orańskiej w Kamieńcu Ząbkowickim: widok z lotu ptaka na neogotycką bryłę, tarasy i otaczający park.\n\nNajlepsze kadry zwykle wychodzą z tarasów i ze wzniesień w parku — stamtąd widać pełną skalę założenia i kontrast między masywną architekturą a zielenią Ziemi Kłodzkiej.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Pałac Marianny Orańskiej?',
          answer:
            'W Kamieńcu Ząbkowickim (powiat ząbkowicki, woj. dolnośląskie), na Ziemi Kłodzkiej — ok. 60 km od Wrocławia i ok. 30 km od Ząbkowic Śląskich.',
        },
        {
          question: 'Ile czasu zaplanować na zwiedzanie pałacu?',
          answer:
            'Zwykle 1,5–3 godziny: ok. godzina na wnętrza, reszta na tarasy i park. Bez pośpiechu spokojnie możesz tu spędzić pół dnia.',
        },
        {
          question: 'Czy warto przyjechać poza sezonem?',
          answer:
            'Tak — architektura robi wrażenie przez cały rok, a poza szczytem sezonu bywa spokojniej. Wnętrza mogą mieć ograniczone godziny zwiedzania zimą — sprawdź to przed wyjazdem.',
        },
        {
          question: 'Czy pałac nadaje się na zdjęcia i wycieczkę rodzinną?',
          answer:
            'Tak — tarasy i park to świetne tło do fotografii, a spacer po ogrodach jest łatwy dla rodzin. Wnętrza wymagają spokojniejszego tempa; przy schodach na tarasach warto uważać z małymi dziećmi.',
        },
        {
          question: 'Jak dojechać do Kamieńca Ząbkowickiego z Wrocławia?',
          answer:
            'Najwygodniej samochodem (ok. 1 godz. jazdy przez Dzierżoniów lub trasą na Ząbkowice Śląskie). Alternatywnie: pociąg do stacji Kamieniec Ząbkowicki i krótki dojazd do pałacu.',
        },
      ],
    },
    highlights: [
      'Neogotycki pałac według koncepcji Karla Friedricha Schinkla',
      'Rozległe tarasy i park krajobrazowy z widokiem na okolicę',
      'Jedna z najbardziej rozpoznawalnych rezydencji Ziemi Kłodzkiej',
    ],
    practicalInfo: {
      visitDuration: 'Zwykle 1,5–3 godziny (łatwo dłużej przy spokojnym spacerze po parku).',
      howToGetThere:
        'Kamieniec Ząbkowicki, pow. ząbkowicki — ok. 60 km od Wrocławia; najwygodniej samochodem, w okolicy stacja kolejowa.',
      parking: 'Parking w pobliżu wejścia; w sezonie i w weekendy warto przyjechać wcześniej.',
      accessibility: 'Tarasy i park: sporo chodzenia, schody i nierówne nawierzchnie; wnętrza — miejscami wąskie przejścia.',
      goodToKnow: [
        'Połącz wizytę z Ząbkowicami Śląskimi, Bardem Śląskim lub trasą w Góry Stołowe.',
        'Godziny zwiedzania i cennik sprawdź przed wyjazdem — mogą się różnić w zależności od sezonu.',
        'Na zdjęcia najlepiej rano lub późnym popołudniem, gdy światło podkreśla detale neogotyku.',
      ],
    },
    seo: {
      title: 'Pałac Marianny Orańskiej – Kamieniec Ząbkowicki | zwiedzanie, tarasy, zdjęcia',
      description:
        'Pałac Marianny Orańskiej w Kamieńcu Ząbkowickim: neogotycka architektura, tarasy, park i praktyczne wskazówki przed wizytą. Zdjęcia, dojazd z Wrocławia i plan zwiedzania.',
      keywords: [
        'Pałac Marianny Orańskiej',
        'pałac Marianny Orańskiej',
        'Kamieniec Ząbkowicki',
        'Kamieniec Ząbkowicki pałac',
        'Marianna Orańska',
        'neogotyk',
        'Ziemia Kłodzka',
        'tarasy Kamieniec Ząbkowicki',
        'zwiedzanie pałacu',
        'Dolny Śląsk',
        'wycieczka z Wrocławia',
        'atrakcje Dolnego Śląska',
      ],
      image: palacMariannyFeaturedPhoto?.src ?? palacMariannyPhotos[0]?.src,
    },
    locationLabel: 'Kamieniec Ząbkowicki',
    lat: 50.5224,
    lng: 16.8796,
  },
  {
    id: 4,
    name: 'Muzeum Bitwy pod Legnicą',
    category: 'Muzeum',
    region: 'Legnickie Pole',
    description:
      'Muzeum Bitwy pod Legnicą w Legnickim Polu — ekspozycja o starciu z 1241 roku, kontekście mongolskim i historii regionu.',
    longDescription:
      'Muzeum Bitwy pod Legnicą w Legnickim Polu to konkretny przystanek na mapie historii Dolnego Śląska: bitwa 9 kwietnia 1241, książę Henryk II Pobożny, tatarski najazd i miejsce, które do dziś pamięta o jednym z kluczowych epizodów średniowiecza w Europie Środkowej.',
    article: {
      published: true,
      intro:
        'Bitwa pod Legnicą z 1241 roku to jeden z tych momentów, które wracają w każdej opowieści o średniowiecznym Śląsku. W Legnickim Polu możesz zobaczyć ją nie tylko na mapie w podręczniku, ale w muzeum, które porządkuje tło, przebieg i skutki starcia w czytelnej, konkretnej ekspozycji.\n\nTo dobry punkt na krótszy przystanek w trasie po Legnicy i okolicach — bez nadmiaru „wypełniaczy”, za to z historią, która realnie zmieniła region. Jeśli lubisz średniowiecze, bitwy i kontekst, który pomaga zrozumieć dzisiejszy krajobraz Dolnego Śląska, Legnickie Pole warto mieć na liście.',
      sections: [
        {
          id: 'legnickie-pole-to',
          title: 'Legnickie Pole i Muzeum Bitwy pod Legnicą',
          body:
            'Legnickie Pole leży kilka kilometrów na wschód od Legnicy, w powiecie legnickim. To właśnie tu, według tradycji i badań, rozegrała się bitwa między wojskami księcia Henryka II Pobożnego a oddziałami mongolskimi (tatarskimi) 9 kwietnia 1241 roku.\n\nMuzeum Bitwy pod Legnicą działa w zabytkowym kompleksie klasztorno-kościelnym, który sam w sobie jest częścią historii miejsca — benedyktyni i później inne zgromadzenia przez wieki budowali tu pamięć o bitwie. Dziś ekspozycja łączy archeologię, makiety, militaria i narrację o tym, jak jedno starcie wpłynęło na Śląsk i Europę Środkową.',
        },
        {
          id: 'historia',
          title: 'Bitwa 1241 — co warto wiedzieć przed zwiedzaniem',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'ekspozycja',
          body:
            'Wiosna 1241 roku. Po śmierci Czyngis-chana mongolskie armie ruszyły na zachód. Jedna z kolumn uderzyła w Polskę — m.in. przez Kraków i Śląsk. Henryk II Pobożny zebrał rycerstwo śląskie, małopolskie i posiłki z innych ziem, by zatrzymać najeźdźców pod Legnicą.\n\nBitwa zakończyła się klęską wojsk chrześcijańskich. Zginął sam Henryk II. Mongolowie nie poszli jednak dalej na zachód w pełnej sile — wycofali się, a pamięć o starciu na długo wpisała się w legendę i tożsamość regionu.\n\nW Legnickim Polu przez stulecia budowano kult pamięci o poległych: kościół, klasztor, kaplice. Muzeum jest współczesną kontynuacją tej opowieści — zamiast samej legendy dostajesz fakty, kontekst i ekspozycję, którą da się przejść w sensownym czasie.',
        },
        {
          id: 'ekspozycja',
          title: 'Co zobaczysz w muzeum',
          body:
            'Ekspozycja prowadzi przez tło najazdu, przebieg bitwy i jej skutki. Zobaczysz m.in.:\n• kontekst polityczny i militarny XIII wieku;\n• broń, militaria i rekonstrukcje wyposażenia;\n• makiety i mapy pomagające zrozumieć pole bitwy;\n• wątek pamięci o starciu w krajobrazie Legnickiego Pola.\n\nTempo zwiedzania jest swobodne — możesz przejść całość w około godzinę albo zostać dłużej przy wybranych stanowiskach. Jeśli jedziesz z dziećmi, warto wcześniej powiedzieć „po co tu jedziemy” — wtedy makiety i broń działają mocniej niż sucha chronologia.\n\nPo muzeum zostaw chwilę na otoczenie: kościół i teren wokół kompleksu też opowiadają historię miejsca.',
        },
        {
          id: 'plan-wizyty',
          title: 'Jak zaplanować wizytę',
          body:
            'Na Muzeum Bitwy pod Legnicą zaplanuj 45–90 minut. To dobry przystanek w trasie, niekoniecznie cel całego dnia — choć w połączeniu z Legnicą spokojnie wypełnisz pół dnia.\n\nDojazd: Legnickie Pole leży ok. 70 km na zachód od Wrocławia i kilka kilometrów od Legnicy. Najwygodniej samochodem; z Legnicy dojazd zajmuje kilkanaście minut.\n\nDobry plan dnia:\n• rano muzeum w Legnickim Polu;\n• po południu Legnica (zamek, rynek, muzea);\n• albo dłuższa trasa w stronę Lubiąża albo Wałbrzycha.\n\nPrzed wyjazdem sprawdź godziny otwarcia i cennik — harmonogram może się różnić w zależności od sezonu i dni wolnych.',
        },
        {
          id: 'galeria',
          title: 'Galeria i klimat miejsca',
          body:
            'Legnickie Pole to nie tylko gabloty — to krajobraz, w którym bitwa została zapamiętana przez wieki. Kościół, klasztor i muzeum tworzą spójną całość, którą najlepiej zobaczyć na spokojnie, bez pośpiechu między kolejnymi punktami trasy.\n\nNa zdjęcia dobrze działa miękkie światło i detale architektury sakralnej wokół muzeum. Jeśli chcesz „złapać klimat” miejsca, zostań chwilę na zewnątrz po wyjściu z ekspozycji.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Muzeum Bitwy pod Legnicą?',
          answer:
            'W Legnickim Polu (powiat legnicki, woj. dolnośląskie) — kilka kilometrów na wschód od Legnicy i ok. 70 km na zachód od Wrocławia.',
        },
        {
          question: 'Ile czasu zaplanować na wizytę?',
          answer:
            'Zwykle 45–90 minut na ekspozycję. Z otoczeniem i spokojnym tempem spokojnie możesz zostać dłużej.',
        },
        {
          question: 'Czy muzeum nadaje się dla dzieci?',
          answer:
            'Tak — makiety, militaria i czytelna narracja działają na młodszych zwiedzających, zwłaszcza gdy wcześniej opowiesz, o jaką bitwę chodzi.',
        },
        {
          question: 'Czy da się połączyć z Legnicą w jednej wycieczce?',
          answer:
            'Tak — to naturalny zestaw. Legnickie Pole rano, Legnica po południu to klasyczny plan na pół dnia lub cały dzień z Wrocławia.',
        },
        {
          question: 'Kiedy była bitwa pod Legnicą?',
          answer:
            '9 kwietnia 1241 roku. Starcie między wojskami Henryka II Pobożnego a oddziałami mongolskimi to jeden z kluczowych epizodów średniowiecznej historii Śląska.',
        },
      ],
    },
    highlights: [
      'Ekspozycja o bitwie 1241 i najazdach mongolskich na Śląsk',
      'Konkretna dawka historii w sensownym czasie (45–90 min)',
      'Dobry przystanek w trasie z Legnicą lub Lubiążem',
    ],
    practicalInfo: {
      visitDuration: 'Zwykle 45–90 minut.',
      howToGetThere:
        'Legnickie Pole, pow. legnicki — kilka km od Legnicy, ok. 70 km od Wrocławia; najwygodniej samochodem.',
      parking: 'Parkowanie w okolicy muzeum i centrum miejscowości (zależnie od dnia).',
      accessibility: 'Budynek zabytkowy — warto liczyć się z progami i schodami; tempo zwiedzania swobodne.',
      goodToKnow: [
        'Połącz wizytę z Legnicą, Lubiążem albo trasą w stronę Wałbrzycha.',
        'Godziny otwarcia i cennik sprawdź przed wyjazdem.',
        'Z dziećmi warto wcześniej opowiedzieć kontekst bitwy — wtedy ekspozycja „klika” mocniej.',
      ],
    },
    seo: {
      title: 'Muzeum Bitwy pod Legnicą – Legnickie Pole | zwiedzanie, historia 1241',
      description:
        'Muzeum Bitwy pod Legnicą w Legnickim Polu: bitwa 1241, ekspozycja, ile trwa wizyta, dojazd z Wrocławia i plan zwiedzania z Legnicą.',
      keywords: [
        'Muzeum Bitwy pod Legnicą',
        'Legnickie Pole',
        'bitwa pod Legnicą',
        'bitwa 1241',
        'Henryk II Pobożny',
        'historia Dolnego Śląska',
        'Legnica atrakcje',
        'zwiedzanie',
        'Dolny Śląsk',
        'wycieczka z Wrocławia',
      ],
    },
    locationLabel: 'Legnickie Pole',
    lat: 51.144078,
    lng: 16.242154,
  },
  {
    id: 5,
    name: 'Arboretum Wojsławice',
    category: 'Natura',
    region: 'Niemcza i okolice',
    description:
      'Ogród botaniczny koło Niemczy: Arboretum Wojsławice (UWr), park dendrologiczny pod Wrocławiem — godziny otwarcia 2026, bilety, czas zwiedzania i zdjęcia z 62 ha ogrodu.',
    longDescription:
      'Arboretum Wojsławice (Wojsławice, gmina Niemcza) to ogród botaniczny pod Wrocławiem: filia UWr z alejkami, stawami, hortensjami i rododendronami. Sezon 2026, cennik i praktyczne informacje przed wizytą.',
    photos: arboretumWojslawicePhotos,
    galleryPhotos: arboretumWojslawiceGalleryPhotos,
    article: {
      published: true,
      intro:
        'Ten zapach czułam długo po powrocie do Wrocławia, to lekkie powietrze, te kolory dookoła. Kładąc się do łóżka po prostu chciałam być z powrotem w Arboretum Wojsławice. Co takiego niezwykłego tam znajdziecie i co warto zobaczyć?\n\nTo ogród botaniczny koło Niemczy — najbliższe Wrocławowi duże arboretum na Dolnym Śląsku. Ponad 62 hektary filii Ogrodu Botanicznego Uniwersytetu Wrocławskiego w parku Wojsławice na Wzgórzach Niemczańskich. Z góry widać „Serce Ogrodu” i sieć alejek; wiosną całe arboretum wygląda jak zielona mozaika z wstawkami bieli i różu. Wrocławianie często jadą tu „na godzinkę”, a zostają na pół dnia, bo przy hortensjach, rododendronach, stawach i kolekcjach narodowych co kilkaset metrów jest powód, by się zatrzymać.',
      sections: [
        {
          id: 'wojslawice-to',
          title: 'Arboretum Wojsławice: ogród botaniczny przy Niemczy',
          body:
            'Arboretum Wojsławice (Wojsławice, gmina Niemcza) to filia Ogrodu Botanicznego Uniwersytetu Wrocławskiego — ogród botaniczny Niemcza i jeden z największych ogrodów dendrologicznych w Polsce, ok. 50 km na południe od Wrocławia. Na ponad 62 hektarach czekają zabytkowy park Wojsławice, rabaty bylin, sad starych odmian czereśni i rozległe kolekcje drzew i krzewów z różnych stref klimatycznych.\n\nTo nie jest „krótki spacer po parku miejskim”. Arboretum Niemcza służy nauce, ochronie gatunków i edukacji, ale dla zwiedzających oznacza to przede wszystkim bardzo dopracowany ogród botaniczny pod Wrocławiem: alejki, pergole, ławki, stawy, place zabaw i trasy przystosowane także dla osób z ograniczoną mobilnością. Szukając atrakcji w okolicy Niemczy, trafiasz tu na miejsce, które łączy mapę kolekcji z ogrodem do fotografowania i spokojnego spaceru — popularną wycieczkę jednodniową z Wrocławia.',
        },
        {
          id: 'historia',
          title: 'Krótka historia ogrodu',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'kolekcje',
          body:
            'Historia ogrodu sięga 1811 roku, kiedy właściciele majątku sprowadzili tu pierwsze egzotyczne gatunki z Ameryki Północnej. Przez dekady teren był prywatnym ogrodem pałacowym, a po 1945 roku przechodził kolejne etapy zagospodarowania.\n\nPrzełom nastąpił w 1988 roku, gdy arboretum stało się filią Ogrodu Botanicznego UWr. Od tego czasu powstały m.in. narodowe kolekcje roślin, dziś jedna z wizytówek miejsca.',
        },
        {
          id: 'kolekcje',
          title: 'Co zobaczyć w Arboretum Wojsławice: kolekcje i zakątki',
          body:
            'W arboretum Wojsławice koło Niemczy na mapie warto zaznaczyć m.in.:\n• narodowe kolekcje różaneczników, hortensji, piwonii, barwinków, liliowców i bukszpanów;\n• „Serce Ogrodu”, centralną część założenia z charakterystyczną kompozycją;\n• stawy, cieniste zagajniki z paprociami i otwarte rabaty pełne bylin;\n• sad starych odmian czereśni;\n• indiański wigwam i miejsca na piknik na własnym kocu.\n\nLatem, zwłaszcza w sierpniu, robi furorę Narodowa Kolekcja Hortensji, ponad 900 gatunków i odmian, w tym wiele unikatów. Wiosną dominują magnolie, azalie i rododendrony; jesienią liście i owoce w sadzie. To główne powody, dla których ogród botaniczny pod Niemczą przyciąga ogrodników i turystów z całego regionu.',
        },
        {
          id: 'kalendarz',
          title: 'Arboretum Wojsławice: godziny otwarcia 2026',
          body:
            'W sezonie 2026 Arboretum Wojsławice zaprasza od 11 kwietnia do 25 października — ogród botaniczny koło Niemczy jest wtedy otwarty codziennie, także w soboty, niedziele i święta.\n\nGodziny otwarcia kas: 9:00–18:00. Teren można zwiedzać do ok. 20:00 (ostatni gość wychodzi przez furtę-kołowrót). To aktualny harmonogram na stronie arboretumwojslawice.pl — przed wyjazdem warto go potwierdzić, bo przy burzach ogród może być zamknięty.\n\nKażda pora sezonu ma inny charakter: wiosna to kwitnienie i świeża zieleń, lato pełnia rabat i hortensje, jesień spokojniejsze światło i barwy liści. Kalendarz kwitnienia publikuje arboretum online — przydatny, jeśli celujesz w konkretną kolekcję.',
        },
        {
          id: 'zwiedzanie',
          title: 'Arboretum Wojsławice: czas zwiedzania',
          body:
            'Na Arboretum Wojsławice zaplanuj minimum 2–3 godziny; bez pośpiechu spokojnie możesz tu spędzić pół dnia, a przy hortensjach i spokojnym tempie — nawet 4 godziny i więcej. Czas zwiedzania zależy od tempa i tego, ile kolekcji chcesz obejść.\n\nW kasie dostaniesz mapę (często w cenie biletu), na 62 hektarach naprawdę się przydaje. Trasy są spacerowe: możesz iść pętlą wokół „Serca Ogrodu”, obejść kolekcje narodowe albo wybrać krótszą trasę po głównych alejkach. Weź wodę i wygodne buty — wzniesienia na Wzgórzach Niemczańskich szybko sumują się w liczbę kroków.\n\nW weekendy otwarcia sezonu, na kiermaszach i w szczycie kwitnienia bywa tłoczniej; spokojniejsze zdjęcia arboretum i mniej ludzi zwykle są rano w dni powszednie.',
        },
        {
          id: 'praktyka',
          title: 'Bilety Wojsławice, cennik i dojazd',
          body:
            'Dojazd: Wojsławice (gmina Niemcza), ok. 50 km na południe od Wrocławia — ogród botaniczny pod Wrocławiem, najwygodniej samochodem (DK8 lub trasa przez Dzierżoniów Śląski i Piłę Rudy).\n\nBilety Arboretum Wojsławice kupisz w kasie (9:00–18:00) lub online na bilety.arboretumwojslawice.pl. Cennik ogrodu botanicznego Wojsławice na sezon 2026 (sprawdź aktualność na arboretumwojslawice.pl/info/cennik/):\n• bilet normalny jednorazowy — 30 zł;\n• bilet ulgowy jednorazowy — 20 zł;\n• bilet wielokrotny (5 wejść) — 120 zł normalny / 80 zł ulgowy;\n• bilet sezonowy imienny — 260 zł normalny / 160 zł ulgowy.\n\nW cenie biletu: parking, toalety, Wi‑Fi, wystawy, kiermasze i wykłady w dniu wizyty. W sezonie odbywają się też koncerty, wykłady i spacery z przewodnikiem (po wcześniejszym umówieniu).',
        },
        {
          id: 'galeria',
          title: 'Arboretum Wojsławice: zdjęcia z ogrodu',
          body:
            'Poniżej galeria zdjęć Arboretum Wojsławice — arboretum zdjęcia z rododendronów, alejek o zachodzie słońca, stawu folwarcznego, kolekcji roślin i zakątek parku Wojsławice na Wzgórzach Niemczańskich.\n\nNajlepsze światło na zdjęcia to wczesny poranek albo późne popołudnie, wtedy ogród botaniczny koło Niemczy jest spokojniejszy i bardziej „miękki” w kadrze. Na górze artykułu też znajdziesz zdjęcia wyróżniające — m.in. widok z lotu ptaka na całe arboretum.',
        },
      ],
      faq: [
        {
          question: 'Jakie są godziny otwarcia Arboretum Wojsławice w 2026 roku?',
          answer:
            'W 2026 sezon trwa od 11 kwietnia do 25 października. Kasy czynne codziennie 9:00–18:00, teren można zwiedzać do ok. 20:00. Aktualne godziny otwarcia i ewentualne zamknięcia (np. przy burzy) sprawdź na arboretumwojslawice.pl.',
        },
        {
          question: 'Ile kosztują bilety do Arboretum Wojsławice (cennik 2026)?',
          answer:
            'Bilet normalny jednorazowy kosztuje 30 zł, ulgowy 20 zł. Są też bilety wielokrotne (od 80 zł) i sezonowe (od 160 zł). Wojsławice bilety kupisz w kasie lub na bilety.arboretumwojslawice.pl — pełny cennik ogrodu botanicznego Wojsławice na arboretumwojslawice.pl/info/cennik/.',
        },
        {
          question: 'Gdzie jest ogród botaniczny koło Niemczy i Arboretum Wojsławice?',
          answer:
            'Arboretum leży w Wojsławicach (gmina Niemcza), na Wzgórzach Niemczańskich, ok. 50 km na południe od Wrocławia — to ogród botaniczny Niemcza i filia Ogrodu Botanicznego UWr. Dojazd najwygodniej samochodem (DK8 lub trasa przez Dzierżoniów Śląski).',
        },
        {
          question: 'Ile trwa zwiedzanie Arboretum Wojsławice (czas zwiedzania)?',
          answer:
            'Minimum 2–3 godziny na główne alejki i kolekcje; bez pośpiechu spokojnie spędzisz tu 4 godziny i więcej.',
        },
        {
          question: 'Czy to dobra wycieczka z Wrocławia (arboretum pod Wrocławiem)?',
          answer:
            'Tak — to najpopularniejszy ogród botaniczny pod Wrocławiem, ok. godziny jazdy samochodem. Wielu mieszkańców szuka „arboretum Wrocław” i trafia właśnie tu, do Wojsławic koło Niemczy.',
        },
        {
          question: 'Gdzie zobaczyć zdjęcia Arboretum Wojsławice?',
          answer:
            'Na tej stronie: galeria zdjęć na górze artykułu i dodatkowa galeria na końcu tekstu — rododendrony, alejki, stawy i widoki z lotu ptaka.',
        },
        {
          question: 'Czy Arboretum Wojsławice nadaje się dla rodzin z dziećmi?',
          answer:
            'Tak, są place zabaw i dużo przestrzeni do spaceru w parku Wojsławice; przy stawach i na schodach warto mieć dzieci pod okiem.',
        },
        {
          question: 'Na co warto przyjechać latem?',
          answer:
            'Latem, zwłaszcza w sierpniu, kwitnie m.in. Narodowa Kolekcja Hortensji — jedna z największych atrakcji ogrodu botanicznego przy Niemczy.',
        },
      ],
    },
    highlights: [
      'Godziny otwarcia 2026: 11 IV–25 X, kasa 9:00–18:00, zwiedzanie do 20:00',
      'Ogród botaniczny koło Niemczy, 62 ha, ok. godzina od Wrocławia — bilety od 20 zł',
      'Arboretum Wojsławice: hortensje, rododendrony, zdjęcia i czas zwiedzania 2–4 h',
    ],
    practicalInfo: {
      visitDuration: 'Czas zwiedzania: najczęściej 2–4 godziny (łatwo dłużej przy spokojnym tempie).',
      howToGetThere:
        'Wojsławice, gm. Niemcza — ogród botaniczny pod Wrocławiem, ok. 50 km; najwygodniej samochodem (DK8 lub trasa przez Dzierżoniów Śląski).',
      parking: 'Parking przy obiekcie, w sezonie w cenie biletu (sprawdź aktualny cennik na arboretumwojslawice.pl).',
      accessibility: 'Część tras przystosowana dla osób z ograniczoną mobilnością; miejscami wzniesienia i nierówne ścieżki.',
      goodToKnow: [
        'Godziny otwarcia i cennik 2026: arboretumwojslawice.pl/info/cennik/. Bilety też na bilety.arboretumwojslawice.pl.',
        'W szczycie sezonu i w weekendy kiermaszowe warto przyjechać wcześnie rano — spokojniejsze zdjęcia i mniej tłumu.',
      ],
    },
    seo: {
      title:
        'Arboretum Wojsławice 2026: godziny otwarcia, bilety i cennik | ogród botaniczny koło Niemczy',
      description:
        'Arboretum Wojsławice — ogród botaniczny koło Niemczy pod Wrocławiem. Godziny otwarcia 2026 (11 IV–25 X), bilety od 20 zł, czas zwiedzania, zdjęcia, dojazd i cennik parku Wojsławice.',
      keywords: [
        'arboretum wojsławice godziny otwarcia 2026',
        'arboretum wojsławice godziny otwarcia',
        'ogród botaniczny koło niemczy',
        'arboretum wojsławice czas zwiedzania',
        'arboretum wojsławice zdjęcia',
        'wojsławice ogród botaniczny godziny otwarcia',
        'arboretum zdjęcia',
        'arboretum wojsławice bilety',
        'wojsławice bilety',
        'niemcza ogród botaniczny godziny otwarcia',
        'ogród botaniczny wojsławice cennik',
        'park wojsławice',
        'arboretum wojsławice wojsławice niemcza',
        'arboretum wrocław',
        'ogród botaniczny pod wrocławiem',
        'arboretum niemcza',
        'ogród botaniczny niemcza',
        'ogród niemcza',
        'niemcza ogród botaniczny',
        'Arboretum Wojsławice',
        'ogród botaniczny Wojsławice',
        'bilety arboretum Wojsławice',
        'dojazd arboretum Wojsławice',
        'wycieczka z Wrocławia',
        'Dolny Śląsk',
      ],
      image: arboretumWojslawiceFeaturedPhoto?.src,
    },
    locationLabel: 'Wojsławice (gmina Niemcza)',
    lat: 50.7292,
    lng: 16.8314,
  },
  {
    id: 6,
    name: 'Rezerwat Wodospad Wilczki',
    category: 'Natura',
    region: 'Międzygórze',
    description:
      'Wodospad Wilczki w rezerwacie przyrody koło Międzygórza — jeden z najwyższych wodospadów Sudetów, krótkie dojście i efekt „na już” w Masywie Śnieżnika.',
    longDescription:
      'Rezerwat Wodospad Wilczki to jedna z najpopularniejszych atrakcji Ziemi Kłodzkiej: ok. 22 metry spadającej wody, zielony wąwóz i krótka, łatwa ścieżka z Międzygórza. Idealny przystanek na wycieczce w Masywie Śnieżnika.',
    photos: wodospadWilczkiPhotos,
    article: {
      published: true,
      intro:
        'Szukasz atrakcji, która daje duży efekt przy małym wysiłku? Wodospad Wilczki koło Międzygórza to właśnie taki punkt — krótkie dojście, a na końcu kaskada wody spadająca z wysokości ok. 22 metrów w zielonym wąwozie Masywu Śnieżnika.\n\nTo jeden z najwyższych wodospadów w polskich Sudetach i obowiązkowy przystanek na mapie Ziemi Kłodzkiej. Nie musisz planować całodniowej wyprawy: podjazd, spacer, zdjęcia przy wodospadzie i możesz jechać dalej — na Śnieżnik, do Kłodzka albo w stronę czeskiej granicy.',
      sections: [
        {
          id: 'miedzygorze-to',
          title: 'Międzygórze i rezerwat Wodospad Wilczki',
          body:
            'Międzygórze to malownicza wieś w powiecie kłodzkim, u podnóża Masywu Śnieżnika, w dolinie potoku Wilczka. Właśnie tu, kilka minut spacerem od centrum, znajduje się rezerwat przyrody „Wodospad Wilczki” — chroniony teren o powierzchni kilkudziesięciu hektarów z jednym z najbardziej rozpoznawalnych wodospadów na Dolnym Śląsku.\n\nWodospad Wilczki powstał na skalnym progu, gdzie Wilczka tworzy efektowną kaskadę w zielonym, wilgotnym wąwozie. Wiosną i po deszczach woda jest najobfitsza, latem — spokojniejsza, ale klimat miejsca zostaje ten sam: cień drzew, szum wody i ścieżki prowadzące wzdłuż potoku.',
        },
        {
          id: 'historia',
          title: 'Rezerwat i ochrona przyrody',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'dojscie',
          body:
            'Rezerwat „Wodospad Wilczki” utworzono w 1958 roku, by chronić unikalny fragment doliny Wilczki wraz z wodospadem i otaczającymi zbiorowiskami leśnymi. To jeden z najstarszych rezerwatów przyrody w regionie.\n\nNa terenie rezerwatu rosną m.in. rzadkie gatunki paproci i mchy, typowe dla wilgotnych, zacienionych wąwozów górskich. Dlatego warto trzymać się wyznaczonych ścieżek — chronisz przyrodę i unikasz śliskich, niestabilnych skał przy samym wodospadzie.\n\nDziś Wodospad Wilczki to jednocześnie atrakcja turystyczna i miejsce edukacji przyrodniczej — tablice informacyjne i oznakowanie szlaków pomagają zrozumieć, co wokół ciebie rośnie i dlaczego ten teren jest chroniony.',
        },
        {
          id: 'dojscie',
          title: 'Dojście i tempo zwiedzania',
          body:
            'Dojście do Wodospadu Wilczki z Międzygórza jest krótkie i stosunkowo łatwe — to jeden z głównych powodów, dla których miejsce jest tak popularne. Nie potrzebujesz trekkingowych butów na całodniową wyprawę, ale dobra podeszwa przyda się zwłaszcza po deszczu.\n\nNa sam wodospad i powrót zaplanuj 30–60 minut — zależnie od tempa, zdjęć i tego, czy zatrzymasz się na dłużej przy kaskadzie. W sezonie (maj–wrzesień) i w weekendy bywa tłoczno; spokojniejsze warunki zwykle są rano w dni powszednie.\n\nŚcieżka prowadzi przez zielony park i wzdłuż potoku — to przyjemny spacer nawet bez „nagrody” w postaci wodospadu, ale ta na końcu trasy robi wrażenie.',
        },
        {
          id: 'kiedy-jechac',
          title: 'Kiedy jechać i na co zwrócić uwagę',
          body:
            'Najefektowniej Wodospad Wilczki wygląda wiosną — po roztopach i przy większych opadach, kiedy Wilczka niesie więcej wody. Latem kaskada bywa spokojniejsza, ale łatwiej podejść bliżej i zrobić zdjęcia bez mgły z rozpylonej wody.\n\nPrzy samym wodospadzie bywa mokro i ślisko — to wilgotny wąwóz, a kamienie pokryte mchem nie wybaczają lekkiej podeszwy. Trzymaj się wyznaczonych przejść i barierki tam, gdzie jest ustawiona.\n\nTo rezerwat przyrody, więc obowiązuje zakaz zrywania roślin, hałasowania i wchodzenia poza ścieżki. Zostaw miejsce tak, jak je zastałeś — dzięki temu kolejni turyści i przyroda mają z tego pożytek.',
        },
        {
          id: 'plan-wizyty',
          title: 'Jak zaplanować wizytę i dojazd',
          body:
            'Dojazd: Międzygórze leży ok. 100 km na południe od Wrocławia i kilka kilometrów od Lądka-Zdroju. Najwygodniej samochodem — parking w okolicy centrum wsi lub przy drodze w stronę wodospadu (w sezonie warto przyjechać wcześniej).\n\nDobry plan dnia w Masywie Śnieżnika:\n• rano Wodospad Wilczki (zanim zrobi się tłoczno);\n• po południu Śnieżnik, Czarna Góra albo spacer po Międzygórzu;\n• alternatywnie: Kłodzko + Międzygórze w jednej wycieczce z Wrocławia.\n\nWodospad Wilczki świetnie sprawdza się jako krótki przystanek „w drodze” — nie wymaga rezerwacji ani biletów, a efekt jest natychmiastowy. Jeśli masz więcej czasu, połącz go z dłuższą trasą pieszą w okolicy Śnieżnika.',
        },
        {
          id: 'galeria',
          title: 'Galeria zdjęć: Wodospad Wilczki',
          body:
            'Poniżej zdjęcia z rezerwatu Wodospad Wilczki: główna kaskada, park i alejki, wejście do rezerwatu oraz widoki na okolicę Międzygórza w Sudetach.\n\nNa zdjęcia najlepiej działa miękkie światło rankiem albo pochmurny dzień — wtedy woda nie prześwieca tak mocno, a zieleń wąwozu wychodzi żywo w kadrze.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Wodospad Wilczki?',
          answer:
            'W rezerwacie przyrody koło Międzygórza (powiat kłodzki, Ziemia Kłodzka), w Masywie Śnieżnika — ok. 100 km na południe od Wrocławia.',
        },
        {
          question: 'Ile czasu zajmuje wizyta przy wodospadzie?',
          answer:
            'Zwykle 30–60 minut na dojście, oglądanie i powrót. Plus czas dojazdu do Międzygórza.',
        },
        {
          question: 'Czy dojście do Wodospadu Wilczki jest trudne?',
          answer:
            'Nie — to krótka i stosunkowo łatwa ścieżka z Międzygórza, odpowiednia także dla rodzin z dziećmi. Po deszczu bywa ślisko przy samym wodospadzie.',
        },
        {
          question: 'Kiedy wodospad wygląda najefektowniej?',
          answer:
            'Wiosną po roztopach i po większych opadach — wtedy Wilczka niesie najwięcej wody. Latem kaskada bywa mniejsza, ale łatwiej podejść bliżej.',
        },
        {
          question: 'Czy trzeba płacić za wejście do rezerwatu?',
          answer:
            'Nie — wstęp do rezerwatu jest bezpłatny. Płacisz tylko za parking w Międzygórzu (jeśli korzystasz z płatnego miejsca).',
        },
        {
          question: 'Co połączyć z Wodospadem Wilczki w jednej wycieczce?',
          answer:
            'Śnieżnik, Lądek-Zdrój, Kłodzko, Czarna Góra albo spacer po Międzygórzu — to naturalne punkty w Masywie Śnieżnika na pół dnia lub cały dzień.',
        },
      ],
    },
    highlights: [
      'Ok. 22 m wysokości — jeden z najwyższych wodospadów w polskich Sudetach',
      'Krótkie, łatwe dojście z Międzygórza — atrakcja „na już”',
      'Rezerwat przyrody z zielonym wąwozem i szlakiem wzdłuż Wilczki',
    ],
    practicalInfo: {
      visitDuration: '30–60 minut na trasę do wodospadu i z powrotem (plus dojazd).',
      howToGetThere:
        'Międzygórze, pow. kłodzki — ok. 100 km od Wrocławia; najwygodniej samochodem, krótkie dojście piesze z centrum wsi.',
      parking: 'Parkingi w Międzygórzu i przy drodze w stronę rezerwatu; w sezonie warto przyjechać wcześniej.',
      accessibility: 'Ścieżka spacerowa w terenie; przy wodospadzie mokro, ślisko i miejscami schody.',
      goodToKnow: [
        'Połącz wizytę ze Śnieżnikiem, Lądkiem-Zdrojem lub Kłodzkiem.',
        'Rano mniej ludzi i lepsze światło na zdjęcia.',
        'Trzymaj się wyznaczonych ścieżek — to chroniony rezerwat przyrody.',
      ],
    },
    seo: {
      title: 'Wodospad Wilczki – Międzygórze | rezerwat, dojście, zdjęcia, dojazd',
      description:
        'Wodospad Wilczki w rezerwacie koło Międzygórza: ok. 22 m kaskady w Masywie Śnieżnika. Jak dojść, kiedy jechać, dojazd z Wrocławia i zdjęcia z wodospadu.',
      keywords: [
        'Wodospad Wilczki',
        'wodospad Wilczki',
        'Rezerwat Wodospad Wilczki',
        'Międzygórze',
        'Międzygórze atrakcje',
        'Masyw Śnieżnika',
        'Ziemia Kłodzka',
        'Sudety wodospad',
        'dojście wodospad Wilczki',
        'wycieczka z Wrocławia',
        'atrakcje Dolnego Śląska',
        'rezerwat przyrody',
      ],
      image: wodospadWilczkiFeaturedPhoto?.src ?? wodospadWilczkiPhotos[0]?.src,
    },
    locationLabel: 'Międzygórze',
    lat: 50.229167,
    lng: 16.760556,
  },
  {
    id: 7,
    name: 'Wieża widokowa na Dzikowcu',
    category: 'Punkt widokowy',
    region: 'Góry Kamienne',
    description:
      'Wieża widokowa na Dzikowcu Wielkim koło Boguszowa-Gorc — szybki punkt widokowy w Górach Kamiennych z panoramą Sudetów.',
    longDescription:
      'Dzikowiec Wielki to wdzięczny cel na krótszą wycieczkę w Górach Kamiennych: podejście spod ośrodka narciarskiego, wieża widokowa i szerokie panoramy. Idealny „strzał” widokowy bez wielogodzinnej wyrypy — szczególnie przy dobrej pogodzie i widoczności.',
    article: {
      published: true,
      intro:
        'Jeśli chcesz widoki bez wielogodzinnej wyrypy, Dzikowiec Wielki koło Boguszowa-Gorc jest bardzo wdzięczny. Wchodzisz (albo podjeżdżasz w okolice ośrodka), robisz podejście, wchodzisz na wieżę i dostajesz panoramę Gór Kamiennych oraz dalszych pasm Sudetów.\n\nTo czytelny cel na krótszy wypad: nie musisz planować całodniowej wyprawy, a efekt na górze jest konkretny. Najlepiej celować w dobrą pogodę i widoczność — wtedy z platformy widać naprawdę szeroko.',
      sections: [
        {
          id: 'dzikowiec-to',
          title: 'Dzikowiec Wielki i wieża widokowa',
          body:
            'Dzikowiec Wielki (ok. 836 m n.p.m.) leży w Górach Kamiennych, w rejonie Boguszowa-Gorc, na pograniczu Sudetów Środkowych. To jeden z bardziej rozpoznawalnych szczytów okolicy — m.in. dzięki ośrodkowi narciarskiemu na stokach i wieży widokowej na wierzchołku.\n\nWieża daje „drugi poziom” widoku ponad koronami drzew. Przy dobrej pogodzie panorama obejmuje m.in. Góry Kamienne, fragmenty Karkonoszy i inne pasma w okolicy Wałbrzycha. To miejsce, które warto zrobić na spokojnie: wejść, odsapnąć, rozejrzeć się i dopiero schodzić.',
        },
        {
          id: 'historia',
          title: 'Okolica i kontekst miejsca',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'widoki',
          body:
            'Góry Kamienne to pasmo o charakterystycznych, „stożkowych” kształtach i gęstych lasach. Dzikowiec od lat łączy turystykę pieszą z aktywnym wypoczynkiem — latem szlaki i wieża, zimą stoki.\n\nWieża widokowa na szczycie powstała jako odpowiedź na popularność miejsca i potrzebę czytelnego punktu widokowego ponad drzewostanem. Dziś to jeden z najbardziej oczywistych celów „na widok” w okolicy Boguszowa-Gorc i Wałbrzycha.\n\nOkolica zachęca też do dołożenia krótkiej pętli — nie musisz wracać tą samą ścieżką, jeśli masz czas i mapę.',
        },
        {
          id: 'widoki',
          title: 'Co widać z wieży',
          body:
            'Przy dobrej pogodzie widoki są szerokie i „górskie”. Z platformy łapiesz panoramę pasm wokół oraz dalsze horyzonty — to właśnie dlatego Dzikowiec działa jako szybki punkt widokowy, a nie tylko „szczyt do zaliczenia”.\n\nMgła, niska chmura albo silny wiatr potrafią mocno ograniczyć efekt. Jeśli jedziesz specjalnie po zdjęcia panoramy, sprawdź prognozę widoczności, nie tylko temperaturę.\n\nNa samej wieży bywa wietrznie — trzymaj się balustrad, szczególnie z dziećmi. Schody wymagają uwagi przy zejściu.',
        },
        {
          id: 'plan-wizyty',
          title: 'Jak zaplanować wypad na Dzikowiec',
          body:
            'Zaplanuj 1–2 godziny na dojście, wejście na wieżę i powrót. Jeśli chcesz, dołóż spacer po okolicy i zamknij temat w pół dnia.\n\nDojazd: rejon Boguszowa-Gorc, ok. 80–90 km od Wrocławia w stronę Wałbrzycha. Najwygodniej samochodem — parkingi przy ośrodku w okolicy (sezonowo). Stamtąd podejście na szczyt jest czytelne i stosunkowo krótkie.\n\nDobry plan dnia:\n• rano Dzikowiec i wieża;\n• po południu Wałbrzych, Książ albo inny punkt w Górach Kamiennych;\n• zimą — połączenie z jazdą na stoku (jeśli ośrodek działa).\n\nButy terenowe są wskazane. Po deszczu ścieżki bywają śliskie, a zejście ze schodów wieży wymaga uwagi.',
        },
        {
          id: 'galeria',
          title: 'Galeria i kadry z Dzikowca',
          body:
            'Najlepsze kadry zwykle wychodzą z platformy wieży przy czystym powietrzu i miękkim świetle — rano albo późnym popołudniem. Warto też złapać podejście przez las i detale konstrukcji samej wieży.\n\nJeśli pogoda nie dopisuje, Dzikowiec nadal daje satysfakcję z podejścia — panorama po prostu czeka na kolejny wypad.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest wieża widokowa na Dzikowcu?',
          answer:
            'Na Dzikowcu Wielkim w Górach Kamiennych, w rejonie Boguszowa-Gorc (ok. 80–90 km od Wrocławia, w stronę Wałbrzycha).',
        },
        {
          question: 'Ile czasu zaplanować?',
          answer:
            'Najczęściej 1–2 godziny na dojście, wieżę i powrót. Z dodatkowym spacerem spokojnie pół dnia.',
        },
        {
          question: 'Czy trasa jest trudna?',
          answer:
            'Raczej łatwa lub średnia — zależy od wybranego podejścia i tempa. Wieża wymaga wejścia schodami.',
        },
        {
          question: 'Kiedy jechać po najlepsze widoki?',
          answer:
            'Przy dobrej widoczności i bez niskich chmur. Mgła i deszcz mocno ograniczają panoramę z wieży.',
        },
        {
          question: 'Co połączyć z Dzikowcem?',
          answer:
            'Wałbrzych, Zamek Książ, Ścieżkę Hochbergów albo inne szczyty Gór Kamiennych — naturalny zestaw na pół dnia lub cały dzień.',
        },
      ],
    },
    highlights: [
      'Szybki punkt widokowy w Górach Kamiennych',
      'Czytelny cel na krótszy wypad z okolic Wałbrzycha',
      'Okolica nadaje się na pętle spacerowe i aktywny dzień',
    ],
    practicalInfo: {
      visitDuration: 'Najczęściej 1–2 godziny.',
      howToGetThere:
        'Dojazd w rejon Boguszowa-Gorc i podejście na Dzikowiec Wielki; ok. 80–90 km od Wrocławia.',
      parking: 'Parkingi przy ośrodku w okolicy (sezonowo); w weekendy warto przyjechać wcześniej.',
      accessibility: 'Podejście w terenie; wieża wymaga wejścia schodami. Po deszczu bywa ślisko.',
      goodToKnow: [
        'Celuj w dobrą widoczność — bez niej panorama z wieży traci sens.',
        'Połącz z Wałbrzychem, Książem albo Ścieżką Hochbergów.',
        'Na wieży bywa wietrznie — uważaj z dziećmi przy balustradach.',
      ],
    },
    seo: {
      title: 'Wieża widokowa na Dzikowcu – widoki, dojście, dojazd | Góry Kamienne',
      description:
        'Wieża widokowa na Dzikowcu Wielkim koło Boguszowa-Gorc: ile czasu zaplanować, jak dojść, kiedy jechać i co widać z platformy.',
      keywords: [
        'Dzikowiec',
        'wieża widokowa Dzikowiec',
        'Dzikowiec Wielki',
        'Boguszów-Gorce',
        'Góry Kamienne',
        'punkt widokowy',
        'Wałbrzych okolice',
        'Dolny Śląsk',
        'wycieczka z Wrocławia',
      ],
    },
    locationLabel: 'Dzikowiec Wielki (okolice Boguszowa-Gorc)',
    lat: 50.724087,
    lng: 16.205692,
  },
  {
    id: 8,
    name: 'Ścieżka Hochbergów',
    category: 'Szlak / spacer',
    region: 'Wałbrzych',
    description:
      'Ścieżka Hochbergów wokół Zamku Książ — widokowa pętla z wąwozami, mostkami i zielenią w Wałbrzychu.',
    longDescription:
      'Ścieżka Hochbergów to jedna z najciekawszych pętli spacerowych w rejonie Zamku Książ: fragmenty lasu, wąwozy, punkty widokowe i miejsca, które zmieniają klimat co kilkanaście minut. Dobra, gdy chcesz połączyć zabytek z ruchem w terenie i zobaczyć okolicę zamku od innej strony.',
    article: {
      published: true,
      intro:
        'Zamek Książ znasz pewnie od frontu. Ścieżka Hochbergów pokazuje go od strony wąwozów, lasu i zieleni — jako pętlę spacerową, która co chwilę zmienia klimat. To jedna z najfajniejszych tras wokół Wałbrzycha, jeśli chcesz połączyć „zabytek” z prawdziwym ruchem w terenie.\n\nNajprościej zacząć przy Zamku Książ i potraktować trasę jako pętlę. Dostajesz mostki, ścieżki w wąwozie, punkty widokowe i sporo zieleni — bez konieczności planowania wielodniowego trekkingu.',
      sections: [
        {
          id: 'sciezka-to',
          title: 'Ścieżka Hochbergów wokół Książa',
          body:
            'Ścieżka Hochbergów to oznakowana trasa spacerowa w Parku Krajobrazowym Sudetów Wałbrzyskich, w bezpośrednim sąsiedztwie Zamku Książ. Nazwa nawiązuje do rodu Hochbergów — historycznych właścicieli Książa — a sama pętla prowadzi przez tereny, które przez wieki należały do majątku zamkowego.\n\nTrasa łączy odcinki leśne, wąwozy, mostki i punkty, z których widać zamek albo okoliczne wzgórza. To nie jest „płaska promenada” — są podejścia, zejścia i miejsca, gdzie buty terenowe robią różnicę. Za to krajobraz zmienia się często i nie nudzi.',
        },
        {
          id: 'historia',
          title: 'Hochbergowie i krajobraz wokół zamku',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'petla',
          body:
            'Hochbergowie (von Hochberg) przez stulecia kształtowali Książ i jego otoczenie — od rezydencji, przez park, po infrastrukturę wokół zamku. Ścieżka, którą dziś chodzisz, prowadzi przez krajobraz będący częścią tej historii: wąwozy, stoki i tereny leśne przylegające do założenia pałacowo-zamkowego.\n\nDziś trasa służy głównie rekreacji i turystyce pieszej. To sposób, żeby zobaczyć Książ nie tylko z parkingu i dziedzińca, ale z perspektywy, którą kiedyś znały raczej służby majątku i mieszkańcy okolicy.\n\nJeśli lubisz łączyć historię z ruchem, warto przed spacerem (albo po) zajrzeć do samego zamku — wtedy pętla nabiera pełnego kontekstu.',
        },
        {
          id: 'petla',
          title: 'Jak podejść do trasy',
          body:
            'To pętla, więc możesz zacząć w kilku miejscach. Najbardziej intuicyjnie startuje się przy Zamku Książ — wtedy łatwo domknąć spacer i wrócić do punktu wyjścia.\n\nW terenie bywa nierówno. Są odcinki w wąwozie, korzenie, kamienie i podejścia. Stabilne buty są wskazane, szczególnie po deszczu. W sezonie i w weekendy przy zamku bywa tłoczno — na samej ścieżce zwykle jest spokojniej niż na dziedzińcu.\n\nTrzymaj się oznakowania i mapy. Boczne odnogi kuszą, ale łatwo wydłużyć trasę albo zgubić rytm pętli.',
        },
        {
          id: 'warianty',
          title: 'Wariant krótki i pełna pętla',
          body:
            'Jeśli masz mało czasu, wybierz krótszy fragment ścieżki i wróć tą samą drogą — nadal złapiesz klimat wąwozów i zieleni. Jeśli chcesz pełną pętlę, zaplanuj 3–4 godziny spokojnego marszu z przystankami na zdjęcia i odpoczynek.\n\nDobry plan dnia w Wałbrzychu:\n• rano Ścieżka Hochbergów;\n• po południu Zamek Książ (albo odwrotnie — zależnie od kolejek);\n• ewentualnie Stara Kopalnia albo inny punkt w mieście.\n\nWeź wodę. W upał odcinki leśne dają cień, ale podejścia i tak dają o sobie znać.',
        },
        {
          id: 'galeria',
          title: 'Klimat trasy i zdjęcia',
          body:
            'Najciekawsze kadry wychodzą w wąwozach, przy mostkach i w miejscach, skąd widać zamek między drzewami. Miękkie światło i wilgotniejszy dzień potrafią dodać klimatu — byle nie było ślisko na kamieniach.\n\nŚcieżka Hochbergów to trasa, którą warto „poczuć nogami”, a nie tylko zaliczyć. Zwolnij w najładniejszych fragmentach — właśnie po to tu jesteś.',
        },
      ],
      faq: [
        {
          question: 'Gdzie zaczyna się Ścieżka Hochbergów?',
          answer:
            'Najwygodniej przy Zamku Książ w Wałbrzychu. Pętla ma też inne możliwe starty, ale zamek jest najbardziej intuicyjnym punktem.',
        },
        {
          question: 'Ile trwa pełna pętla?',
          answer:
            'Zwykle 3–4 godziny spokojnego marszu z przystankami. Krótsze warianty też są OK — wystarczy zawrócić wcześniej.',
        },
        {
          question: 'Czy trasa jest dla początkujących?',
          answer:
            'Tak, przy rozsądnym tempie. Warto mieć buty terenowe i liczyć się z podejściami oraz nierównym podłożem.',
        },
        {
          question: 'Czy można połączyć ze zwiedzaniem Zamku Książ?',
          answer:
            'Tak — to naturalny zestaw. Spacer i zamek w jednym dniu to klasyczny plan na Wałbrzych.',
        },
        {
          question: 'Na co uważać po deszczu?',
          answer:
            'Na śliskie kamienie, korzenie i odcinki w wąwozie. Po opadach tempo warto obniżyć i trzymać się głównego oznakowania.',
        },
      ],
    },
    highlights: [
      'Pętla z wąwozami, mostkami i zmiennym klimatem wokół Książa',
      'Połączenie spaceru z wizytą przy Zamku Książ',
      'Jedna z najlepszych tras „w terenie” w Wałbrzychu',
    ],
    practicalInfo: {
      visitDuration: 'Najczęściej 3–4 godziny dla pełnej pętli (krótsze warianty też są OK).',
      howToGetThere: 'Start w rejonie Zamku Książ (Wałbrzych); ok. 70–80 km od Wrocławia.',
      parking: 'Parkingi przy zamku i w okolicy; w sezonie bywa tłoczno — warto przyjechać wcześniej.',
      accessibility: 'Szlak terenowy: nierówności, podejścia, wąwozy. Po deszczu ślisko.',
      goodToKnow: [
        'Buty terenowe zdecydowanie pomagają.',
        'Połącz spacer ze zwiedzaniem Zamku Książ.',
        'Weź wodę i zapas czasu — pełna pętla lubi się wydłużać na zdjęciach.',
      ],
    },
    seo: {
      title: 'Ścieżka Hochbergów – pętla wokół Zamku Książ | Wałbrzych',
      description:
        'Ścieżka Hochbergów: widokowa pętla spacerowa wokół Zamku Książ. Ile trwa, jak zaplanować, warianty trasy i praktyczne wskazówki.',
      keywords: [
        'Ścieżka Hochbergów',
        'Zamek Książ',
        'Wałbrzych',
        'szlak spacerowy',
        'pętla Książ',
        'Hochbergowie',
        'Park Krajobrazowy Sudetów Wałbrzyskich',
        'Dolny Śląsk',
        'wycieczka z Wrocławia',
      ],
    },
    locationLabel: 'Wałbrzych (okolice Zamku Książ)',
    lat: 50.842194,
    lng: 16.291567,
  },
  {
    id: 9,
    name: 'Zalew Mietków',
    category: 'Woda / rekreacja',
    region: 'Mietków',
    description:
      'Zalew Mietków (Jezioro Mietkowskie) — największy zbiornik wodny na Dolnym Śląsku: spacer, wiatr, zachody słońca i rekreacja blisko Wrocławia.',
    longDescription:
      'Zalew Mietków to klasyczny wypad za miasto z Wrocławia i okolic. Największe jezioro w województwie dolnośląskim daje przestrzeń na spacer wzdłuż brzegu, odpoczynek nad wodą i zachody słońca. Najlepiej działa przy dobrej pogodzie — szczególnie wieczorem.',
    photos: zalewMietkowPhotos,
    article: {
      published: true,
      intro:
        'To miejsce nie jest „zwiedzaniem”, tylko odpoczynkiem. Jeśli chcesz dzień bez planu, Zalew Mietków (Jezioro Mietkowskie) to jeden z najprostszych wyborów na Dolnym Śląsku: przyjeżdżasz, wybierasz kawałek brzegu i masz gotowe.\n\nNajwiększy zbiornik wodny w województwie robi wrażenie skalą. Przy wietrze klimat bywa bardziej „nadmorski” niż jeziorowy. Wieczorem, gdy słońce schodzi nad wodę, Mietków bywa jednym z najlepszych miejsc na zachód w okolicy Wrocławia.',
      sections: [
        {
          id: 'mietkow-to',
          title: 'Zalew Mietków i Jezioro Mietkowskie',
          body:
            'Zalew Mietków powstał przez spiętrzenie Bystrzycy. Leży w gminie Mietków, ok. 35–40 km na południowy zachód od Wrocławia. Powierzchnia zbiornika liczy kilkaset hektarów — to największe jezioro na Dolnym Śląsku i jeden z najbardziej oczywistych celów rekreacyjnych w regionie.\n\nBrzegi oferują przestrzeń do spaceru, obserwowania wody i spędzania czasu na świeżym powietrzu. Nie szukaj tu muzealnej narracji — Mietków działa jako krajobraz i klimat. Im mniej planu, tym lepiej.',
        },
        {
          id: 'historia',
          title: 'Skąd wziął się zalew',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'co-robic',
          body:
            'Jezioro Mietkowskie to zbiornik retencyjny na Bystrzycy, budowany w latach 70. i 80. XX wieku. Celem była ochrona przeciwpowodziowa i gospodarka wodna — efekt uboczny okazał się turystyczny: duża tafla wody blisko Wrocławia szybko stała się miejscem weekendowych wypadów.\n\nDziś zalew łączy funkcję retencyjną z rekreacją. To ważne, bo warunki (poziom wody, wiatr, dostępność fragmentów brzegu) bywają zmienne — warto traktować Mietków elastycznie i wybierać odcinek brzegu pod pogodę dnia.',
        },
        {
          id: 'co-robic',
          title: 'Co robić na miejscu',
          body:
            'Spacer wzdłuż brzegu, rekreacja i obserwowanie wody — prosto. Przy wietrze klimat robi się bardziej otwarty i „duży”. Jeśli jedziesz z ekipą, łatwo zorganizować piknik (z poszanowaniem zasad i porządku).\n\nPopularne aktywności to m.in.:\n• spacery i spokojne siedzenie nad wodą;\n• fotografia zachodów słońca;\n• rekreacja wodna w sezonie (zależnie od aktualnych zasad i infrastruktury).\n\nSprawdź lokalne zasady przed wejściem do wody albo korzystaniem z konkretnych plaż i pomostów — oferta sezonowa bywa zmienna.',
        },
        {
          id: 'kiedy-jechac',
          title: 'Kiedy najlepiej przyjechać',
          body:
            'W tygodniu jest spokojniej. W weekendy i w sezonie letnim potrafi być tłoczno, więc lepiej przyjechać wcześniej albo celować w późne popołudnie.\n\nZachody słońca to mocna strona Mietkowa — przy pogodzie warto zostać do wieczora. Wiatr bywa silny; weź coś na siebie nawet latem.\n\nZimą i poza sezonem zalew też ma klimat, ale infrastruktura bywa ograniczona. To wtedy bardziej „krajobraz i spacer” niż pełna rekreacja.',
        },
        {
          id: 'plan-wizyty',
          title: 'Jak zaplanować wypad nad zalew',
          body:
            'Czas: od godziny do całego dnia — zależnie od tego, czy chcesz tylko spacer, czy dłuższy wypoczynek.\n\nDojazd: najwygodniej samochodem; zalew jest ok. 40–50 minut od Wrocławia. Parkingi i zjazdy są w okolicy brzegu; w sezonie bywa tłoczno.\n\nDobry plan:\n• po południu dojazd z Wrocławia;\n• spacer i wybór miejsca nad wodą;\n• zachód słońca i powrót.\n\nZostaw miejsce czyste. Duży zbiornik „przyjmuje” dużo ludzi — porządek na brzegu to wspólna odpowiedzialność.',
        },
        {
          id: 'galeria',
          title: 'Galeria: woda, wiatr i zachody',
          body:
            'Najlepsze kadry z Mietkowa to szerokie ujęcia tafli wody, niebo i zachody. Miękkie światło wieczorem robi robotę — zwłaszcza gdy wiatr rysuje falę na powierzchni.\n\nWarto też złapać detale brzegu i przestrzeń „pustego” horyzontu nad wodą — to właśnie ten krajobraz odróżnia Mietków od typowych, małych jezior okolicy.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Zalew Mietków?',
          answer:
            'W gminie Mietków (woj. dolnośląskie), ok. 35–40 km na południowy zachód od Wrocławia — to Jezioro Mietkowskie, największy zbiornik w województwie.',
        },
        {
          question: 'Ile czasu zaplanować?',
          answer:
            'Od 1–2 godzin do całego dnia — zależnie od tego, czy chcesz tylko spacer, czy dłuższy wypoczynek nad wodą.',
        },
        {
          question: 'Kiedy jechać na zachód słońca?',
          answer:
            'Przy pogodzie warto zostać do wieczora. W weekendy przyjedź wcześniej, żeby złapać miejsce parkingowe i spokojniejszy kawałek brzegu.',
        },
        {
          question: 'Czy to dobry wypad z Wrocławia?',
          answer:
            'Tak — jeden z najprostszych. Dojazd samochodem zajmuje zwykle poniżej godziny, a efekt „dużej wody” jest natychmiastowy.',
        },
        {
          question: 'Czy w sezonie bywa tłoczno?',
          answer:
            'Tak, zwłaszcza w weekendy latem. Wtedy lepiej celować w dzień powszedni, wczesny poranek albo późne popołudnie.',
        },
      ],
    },
    highlights: [
      'Największe jezioro na Dolnym Śląsku — blisko Wrocławia',
      'Spacer, przestrzeń i zachody słońca nad wodą',
      'Prosty wypad „bez planu” na pół dnia lub cały dzień',
    ],
    practicalInfo: {
      visitDuration: 'Od 1–2 godzin do całego dnia.',
      howToGetThere:
        'Najwygodniej samochodem; Zalew Mietków jest ok. 35–40 km / 40–50 min od Wrocławia.',
      parking: 'W okolicy są parkingi i zjazdy; w sezonie bywa tłoczno — warto przyjechać wcześniej.',
      accessibility: 'Teren rekreacyjny; dojścia po drogach i ścieżkach zależnie od wybranego fragmentu brzegu.',
      goodToKnow: [
        'Na zachód słońca warto zostać do wieczora przy pogodzie.',
        'Weź coś na wiatr — nad wodą bywa chłodniej niż w mieście.',
        'Zostaw brzeg czysty; w sezonie ruch jest duży.',
      ],
    },
    seo: {
      title: 'Zalew Mietków – Jezioro Mietkowskie | spacer, dojazd, zachody słońca',
      description:
        'Zalew Mietków (Jezioro Mietkowskie): największe jezioro na Dolnym Śląsku. Jak dojechać z Wrocławia, kiedy przyjechać i co robić nad wodą.',
      keywords: [
        'Zalew Mietków',
        'Jezioro Mietkowskie',
        'Mietków',
        'rekreacja Dolny Śląsk',
        'wypad z Wrocławia',
        'zachód słońca',
        'jezioro Dolny Śląsk',
        'spacer nad wodą',
      ],
      image: zalewMietkowPhotos[0],
    },
    locationLabel: 'Mietków (Jezioro Mietkowskie)',
    lat: 50.95861,
    lng: 16.61889,
  },
  {
    id: 10,
    name: 'Szwajcaria Lwówecka',
    category: 'Natura',
    region: 'Dolina Bobru',
    description:
      'Szwajcaria Lwówecka pod Lwówkiem Śląskim — skalny labirynt i punkty widokowe wśród piaskowcowych formacji w Parku Krajobrazowym Doliny Bobru.',
    longDescription:
      'Szwajcaria Lwówecka to zespół piaskowcowych skał i naturalnych formacji w okolicach Lwówka Śląskiego, w Parku Krajobrazowym Doliny Bobru. Świetny pomysł na krótki wypad: teren jest ciekawy, „skałkowy”, a w kilku miejscach trafisz na widoki i przejścia między głazami.',
    photos: szwajcariaLwoweckaPhotos,
    article: {
      published: true,
      intro:
        'To propozycja na szybki spacer z klimatem: trochę lasu, trochę skał, sporo miejsc na zdjęcie. Szwajcaria Lwówecka pod Lwówkiem Śląskim to piaskowcowy labirynt, który działa najlepiej, gdy chcesz zobaczyć coś nietypowego bez całodniowej trasy.\n\nPark Krajobrazowy Doliny Bobru daje tu krótkie podejścia, wąskie przejścia między skałami i punkty widokowe. Najlepiej iść bez pośpiechu, zaglądać w boczne ścieżki i złapać momenty, których nie ma na typowej „promenadzie”.',
      sections: [
        {
          id: 'szwajcaria-to',
          title: 'Szwajcaria Lwówecka i Dolina Bobru',
          body:
            'Szwajcaria Lwówecka leży w okolicach Lwówka Śląskiego, w Parku Krajobrazowym Doliny Bobru (woj. dolnośląskie). Nazwa nawiązuje do malowniczości terenu — piaskowcowe skały, leśne ścieżki i formy erozyjne tworzą krajobraz, który kojarzy się z „małą Szwajcarią” w miniaturze.\n\nTo nie jest wysoki górotwór ani długa wędrówka. To raczej skalny, fotogeniczny spacer z odnogami do punktów widokowych. Właśnie dlatego miejsce tak dobrze działa jako krótki wypad albo przystanek w dłuższej trasie po zachodnim Dolnym Śląsku.',
        },
        {
          id: 'historia',
          title: 'Skąd wzięły się skały',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'spacer',
          body:
            'Piaskowce w okolicy Lwówka Śląskiego to efekt procesów geologicznych i erozji, które przez tysiące lat rzeźbiły formy skalne widoczne dziś na ścieżkach. Region Bobru od dawna był ceniony za krajobraz — stąd też ochrona w ramach parku krajobrazowego.\n\nDla turysty ważne jest jedno: to teren chroniony i kruchy. Trzymanie się ścieżek, nie wchodzenie na niestabilne krawędzie i nie „dokładanie” własnych skrótów chroni formacje i Ciebie.\n\nLwówek Śląski w pobliżu dodaje miejski kontekst — rynek, zabytki i baza na kawę przed albo po spacerze.',
        },
        {
          id: 'spacer',
          title: 'Jak wygląda spacer',
          body:
            'To raczej krótka pętla lub spacer po leśnych ścieżkach z odnogami do skał i punktów widokowych. Po deszczu bywa ślisko na kamieniach, a w wąskich przejściach warto zwolnić.\n\nNajfajniej iść bez pośpiechu: zaglądać w boczne ścieżki, robić zdjęcia i nie gonić „zaliczenia” wszystkich formacji na raz. Kilka dobrze wybranych punktów zwykle daje więcej satysfakcji niż pośpieszne okrążenie całego terenu.\n\nOznakowanie i lokalne mapy pomagają wybrać start. Jeśli jesteś tu pierwszy raz, trzymaj się najbardziej uczęszczanych dojść — łatwiej wrócić do auta.',
        },
        {
          id: 'wskazowki',
          title: 'Wskazówki praktyczne',
          body:
            'Załóż stabilne buty — przy skałach i korzeniach łatwo o poślizg. Jeśli idziesz z dziećmi, pilnuj ich przy krawędziach i w miejscach, gdzie teren opada.\n\nW upał przyda się woda. Poza sezonem warto mieć coś na wiatr (na odsłoniętych fragmentach bywa chłodniej).\n\nCzas: najczęściej 1–2 godziny. To nie jest trasa „na cały dzień”, chyba że łączysz ją z Lwówkiem Śląskim i dalszą trasą w Dolinie Bobru.',
        },
        {
          id: 'plan-wizyty',
          title: 'Jak zaplanować wizytę',
          body:
            'Dojazd: Lwówek Śląski leży ok. 100–110 km na zachód od Wrocławia. Najwygodniej samochodem; potem krótkie dojście lub dojazd w rejon skał.\n\nDobry plan dnia:\n• rano Szwajcaria Lwówecka;\n• po południu Lwówek Śląski (rynek, zabytki);\n• ewentualnie dalsza trasa w Dolinie Bobru albo w stronę Jeleniej Góry.\n\nParking zależy od wybranego startu — w okolicy są miejsca postojowe przy drogach i dojściach. W weekendy bywa więcej ludzi; w tygodniu zwykle spokojniej.',
        },
        {
          id: 'galeria',
          title: 'Galeria: skały i labirynt',
          body:
            'Szwajcaria Lwówecka jest bardzo fotogeniczna: piaskowiec, przejścia między skałami, detale erozji i leśne światło. Najlepiej działa miękkie światło i trochę wilgoci w powietrzu — wtedy faktura skał wychodzi mocniej.\n\nUważaj przy krawędziach podczas robienia zdjęć. Najładniejszy kadr nie jest wart poślizgu.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Szwajcaria Lwówecka?',
          answer:
            'W okolicach Lwówka Śląskiego, w Parku Krajobrazowym Doliny Bobru (woj. dolnośląskie) — ok. 100–110 km na zachód od Wrocławia.',
        },
        {
          question: 'Czy to długa trasa?',
          answer:
            'Nie — to raczej krótki spacer (zwykle 1–2 godziny) z wariantami podejść do skał i punktów widokowych.',
        },
        {
          question: 'Czy trasa nadaje się dla dzieci?',
          answer:
            'Tak, przy uważnym tempie. Pilnuj dzieci przy krawędziach i na śliskich kamieniach, szczególnie po deszczu.',
        },
        {
          question: 'Jakie buty zabrać?',
          answer:
            'Stabilne, z dobrą przyczepnością. Skały, korzenie i wilgoć po opadach nie lubią lekkiej podeszwy.',
        },
        {
          question: 'Co połączyć ze Szwajcarią Lwówecką?',
          answer:
            'Lwówek Śląski oraz dalszą trasę w Dolinie Bobru albo w stronę Jeleniej Góry — naturalny zestaw na pół dnia lub cały dzień.',
        },
      ],
    },
    highlights: [
      'Piaskowcowe skały i skalny „labirynt” pod Lwówkiem',
      'Krótki, bardzo fotogeniczny spacer',
      'Park Krajobrazowy Doliny Bobru — blisko Lwówka Śląskiego',
    ],
    practicalInfo: {
      visitDuration: 'Najczęściej 1–2 godziny.',
      howToGetThere:
        'Dojazd do Lwówka Śląskiego i dojście lub dojazd w rejon skał (Park Krajobrazowy Doliny Bobru); ok. 100–110 km od Wrocławia.',
      parking: 'Zależnie od wybranego startu — w okolicy są miejsca postojowe przy drogach i dojściach.',
      accessibility: 'Leśne ścieżki i skały; miejscami nierówno i ślisko po opadach.',
      goodToKnow: [
        'Buty z przyczepnością obowiązkowe przy skałach.',
        'Połącz spacer z wizytą w Lwówku Śląskim.',
        'Trzymaj się ścieżek — to teren chroniony i kruchy.',
      ],
    },
    seo: {
      title: 'Szwajcaria Lwówecka – skały i spacer pod Lwówkiem Śląskim',
      description:
        'Szwajcaria Lwówecka w Parku Krajobrazowym Doliny Bobru: piaskowcowe skały, krótkie trasy i punkty widokowe. Praktyczne wskazówki i plan wizyty.',
      keywords: [
        'Szwajcaria Lwówecka',
        'Lwówek Śląski',
        'Dolina Bobru',
        'Park Krajobrazowy Doliny Bobru',
        'skały piaskowcowe',
        'spacer',
        'Dolny Śląsk',
        'wycieczka z Wrocławia',
      ],
      image: szwajcariaLwoweckaPhotos[0],
    },
    locationLabel: 'Lwówek Śląski (Park Krajobrazowy Doliny Bobru)',
    lat: 51.1014,
    lng: 15.59404,
  },
  {
    id: 11,
    name: 'Odryw Skalny w Bardo',
    category: 'Natura',
    region: 'Bardo i okolice',
    description:
      'Odryw Skalny w Bardo — widokowy punkt skalny nad doliną Nysy Kłodzkiej z panoramą miasta i okolicznych wzgórz.',
    longDescription:
      'Odryw Skalny w Bardo to krótki, ale bardzo satysfakcjonujący cel spaceru. Podejście nie jest długie, a na miejscu czeka skalna krawędź i szeroki widok na miasto oraz okoliczne wzgórza w przełomie Nysy Kłodzkiej.',
    article: {
      published: true,
      intro:
        'Bardo Śląskie zna się zwykle od dołu — sanktuarium, rynek, przełom Nysy Kłodzkiej. Odryw Skalny pokazuje to samo miasto z góry: skalna krawędź, panorama doliny i krótkie podejście, które daje duży efekt przy małym wysiłku.\n\nNajlepiej wybrać poranek albo spokojniejszy dzień, żeby nacieszyć się widokiem bez tłumu. To dobry punkt na krótki wypad albo część dłuższej trasy po okolicy Barda i Ziemi Kłodzkiej.',
      sections: [
        {
          id: 'bardo-to',
          title: 'Bardo i Odryw Skalny',
          body:
            'Bardo leży w powiecie ząbkowickim, w przełomie Nysy Kłodzkiej — jednym z najbardziej charakterystycznych krajobrazów na styku Sudetów i Przedgórza. Odryw Skalny to punkt widokowy na skalnej krawędzi nad miastem: stąd widać zabudowę, rzekę i okoliczne wzgórza.\n\nNazwa „odryw” dobrze oddaje charakter miejsca — skała i zbocze tworzą naturalny taras widokowy. To nie jest długa wędrówka górska, tylko krótki, satysfakcjonujący spacer z nagrodą w postaci panoramy.',
        },
        {
          id: 'historia',
          title: 'Przełom Nysy Kłodzkiej i kontekst miejsca',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'dojscie',
          body:
            'Przełom Nysy Kłodzkiej koło Barda to efekt erozji rzecznej i rzeźby terenu, która przez wieki kształtowała krajobraz. Miasto rozwijało się w tym wąskim, malowniczym „gardle” doliny — stąd też sanktuarium, mosty i zabudowa wciskająca się między stoki.\n\nPunkty widokowe nad Bardem (w tym Odryw Skalny) od dawna służą mieszkańcom i turystom jako miejsce, z którego widać całą kompozycję: rzekę, miasto i otaczające wzgórza.\n\nDziś to jeden z najprostszych sposobów, żeby „zrozumieć” Bardo przestrzennie — z dołu widzisz detale, z góry całość.',
        },
        {
          id: 'dojscie',
          title: 'Dojście do punktu widokowego',
          body:
            'Podejście jest krótkie i czytelne, ale miejscami teren bywa nierówny. Warto iść spokojnie, szczególnie po deszczu, gdy na kamieniach i korzeniach może być ślisko.\n\nNa dojście, postój i powrót zaplanuj zwykle 1–2 godziny — zależnie od tempa i tego, ile czasu spędzisz na samym punkcie. Szlak prowadzi przez las; oznakowanie w okolicy Barda bywa lokalne, więc warto mieć mapę lub ślad GPS.\n\nPrzy samej krawędzi zachowaj ostrożność. To punkt widokowy, nie plac zabaw — szczególnie z dziećmi trzymaj dystans od krawędzi.',
        },
        {
          id: 'tipy',
          title: 'Tipy praktyczne i plan dnia',
          body:
            'Celuj we wcześniejsze godziny, jeśli chcesz spokojnie zrobić zdjęcia. Po opadach załóż buty z lepszą przyczepnością.\n\nDobry plan dnia w okolicy:\n• rano Odryw Skalny;\n• po południu Bardo (sanktuarium, spacer po mieście);\n• ewentualnie Kamieniec Ząbkowicki albo dalsza trasa w stronę Ziemi Kłodzkiej.\n\nBardo leży ok. 70–80 km na południe od Wrocławia — najwygodniej samochodem. Parkingi są w mieście i przy punktach startowych szlaków.',
        },
        {
          id: 'galeria',
          title: 'Widoki i kadry z Odrywu',
          body:
            'Najmocniejsze kadry to panorama Barda i doliny Nysy Kłodzkiej z krawędzi skały. Miękkie światło rano albo późnym popołudniem podkreśla rzeźbę terenu i zabudowę w dole.\n\nWarto też złapać detale podejścia: las, skałę, perspektywę zejścia wzroku w dolinę. To miejsce, które na zdjęciach „wyjaśnia” Bardę lepiej niż sam rynek.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Odryw Skalny w Bardo?',
          answer:
            'Nad Bardem Śląskim (powiat ząbkowicki), w przełomie Nysy Kłodzkiej — krótki spacer ze szlakiem do punktu widokowego na skalnej krawędzi.',
        },
        {
          question: 'Ile czasu zaplanować?',
          answer:
            'Najczęściej 1–2 godziny (podejście, postój, powrót), zależnie od tempa.',
        },
        {
          question: 'Czy podejście jest trudne?',
          answer:
            'Raczej krótkie i satysfakcjonujące, ale miejscami nierówne. Po deszczu bywa ślisko — stabilne buty pomagają.',
        },
        {
          question: 'Czy punkt nadaje się dla dzieci?',
          answer:
            'Tak, przy uważnym tempie. Przy krawędzi punktu widokowego zachowaj szczególną ostrożność.',
        },
        {
          question: 'Co połączyć z Odrywem Skalnym?',
          answer:
            'Samo Bardo (sanktuarium, miasto), Kamieniec Ząbkowicki albo dalszą trasę po Ziemi Kłodzkiej.',
        },
      ],
    },
    highlights: [
      'Widok na Bard i dolinę Nysy Kłodzkiej',
      'Krótki, atrakcyjny spacer z dużą nagrodą widokową',
      'Fotogeniczny punkt skalny nad przełomem rzeki',
    ],
    practicalInfo: {
      visitDuration: 'Najczęściej 1–2 godziny.',
      howToGetThere:
        'Dojazd do Barda i krótkie podejście szlakiem do punktu widokowego; ok. 70–80 km od Wrocławia.',
      parking: 'Miejsca postojowe w Bardzie i przy punktach startowych szlaków.',
      accessibility: 'Leśna trasa; miejscami stromo i ślisko po opadach. Przy krawędzi zachowaj ostrożność.',
      goodToKnow: [
        'Rano mniej ludzi i lepsze światło na panoramę.',
        'Połącz z Bardem i Kamieńcem Ząbkowickim.',
        'Przy krawędzi — szczególna ostrożność z dziećmi.',
      ],
    },
    seo: {
      title: 'Odryw Skalny w Bardo – punkt widokowy i krótka trasa',
      description:
        'Odryw Skalny w Bardo: panorama doliny Nysy Kłodzkiej, jak dojść, ile czasu zaplanować i o czym pamiętać przed wizytą.',
      keywords: [
        'Odryw Skalny w Bardo',
        'Odryw Skalny',
        'Bardo',
        'Bardo Śląskie',
        'punkt widokowy',
        'Nysa Kłodzka',
        'Dolny Śląsk',
        'szlak',
        'wycieczka z Wrocławia',
      ],
    },
    locationLabel: 'Bardo',
    lat: 50.5056,
    lng: 16.7424,
  },
  {
    id: 12,
    name: 'Radunia',
    category: 'Góry',
    region: 'Masyw Ślęży',
    description:
      'Radunia w Masywie Ślęży — górski wypad blisko Wrocławia: leśne trasy, pętle i konkretna wędrówka bez dalekiego dojazdu.',
    longDescription:
      'Radunia to dobry wybór, gdy chcesz góry „na szybko” bez dalekich dojazdów. Szczyt w Masywie Ślęży (ok. 573 m n.p.m.) oferuje leśne trasy i pętle wokół Sobótki — idealne na trening, spokojny spacer albo pół dnia ruchu blisko Wrocławia.',
    article: {
      published: true,
      intro:
        'Nie zawsze trzeba jechać w Karkonosze, żeby poczuć podejście i las. Radunia w Masywie Ślęży leży blisko Wrocławia i daje konkretną wędrówkę bez dalekiego dojazdu — leśne ścieżki, warianty pętli i satysfakcja z wejścia na szczyt.\n\nNajlepiej potraktować Radunię jako część większej pętli w Masywie Ślęży. To teren, który lubi się z regularnymi wypadami: za każdym razem możesz wybrać inny wariant i inną długość.',
      sections: [
        {
          id: 'radunia-to',
          title: 'Radunia i Masyw Ślęży',
          body:
            'Radunia (ok. 573 m n.p.m.) to jeden z głównych szczytów Masywu Ślęży, na południe od Wrocławia, w rejonie Sobótki. Masyw Ślęży to „góry blisko miasta” — nie najwyższe, ale z charakterem: lasy, skalne wychodnie, szlaki i gęsta sieć pętli.\n\nRadunia bywa wybierana jako cel treningowy albo spokojniejsza alternatywa dla samej Ślęży, gdy chcesz mniej ludzi albo inny wariant trasy. Podejścia są leśne, a satysfakcja z domknięcia pętli — konkretna.',
        },
        {
          id: 'historia',
          title: 'Ślęża, kult i krajobraz',
          skippable: true,
          skipLabel: 'Pomijam historię',
          skipScrollToId: 'trasy',
          body:
            'Masyw Ślęży to jedno z najbardziej symbolicznych miejsc na Dolnym Śląsku — od pradziejów związane z kultem, osadnictwem i krajobrazem „świętej góry”. Ślęża dominuje panoramę, a Radunia i sąsiednie szczyty dopełniają masyw jako teren wędrówek.\n\nDla współczesnego turysty ważne jest to, że wokół Sobótki powstała gęsta sieć szlaków: łatwo złożyć pętlę pod kondycję i pogodę. Radunia jest częścią tej mapy — nie tylko „szczytem do zaliczenia”, ale punktem w większej, leśnej układance.\n\nW weekendy rejon bywa popularny. Jeśli lubisz ciszę, celuj w dzień powszedni albo wcześniejsze godziny.',
        },
        {
          id: 'trasy',
          title: 'Trasy i warianty',
          body:
            'W Masywie Ślęży jest sporo wariantów podejść i pętli. Wybierz trasę pod kondycję i pogodę: po deszczu bywa ślisko, a w upał przyda się woda.\n\nJeśli idziesz pierwszy raz, celuj w czytelny, klasyczny wariant i domknij pętlę bez kombinowania. Doświadczeni mogą łączyć Radunię z innymi szczytami masywu w dłuższy dzień.\n\nCzas: zwykle 2–4 godziny zależnie od długości pętli. To nie jest „kwadrans na punkt widokowy” — to prawdziwa, choć bliska, wędrówka.',
        },
        {
          id: 'praktycznie',
          title: 'Praktycznie: kiedy i jak jechać',
          body:
            'Miejsce działa cały rok, ale najlepszy klimat bywa wiosną i jesienią. W weekendy trafisz na sporo osób — to popularny rejon „blisko miasta”.\n\nDojazd: okolice Sobótki, ok. 35–40 km na południowy zachód od Wrocławia. Najwygodniej samochodem; parkingi przy wejściach na szlaki w weekendy mogą się zapełniać.\n\nDobry plan:\n• poranny start z parkingu przy szlaku;\n• pętla z Radunią;\n• powrót do Sobótki na coś ciepłego do picia.\n\nButy terenowe i woda to minimum. Po opadach błoto na leśnych odcinkach bywa intensywne.',
        },
        {
          id: 'galeria',
          title: 'Klimat lasu i kadry z masywu',
          body:
            'Radunia i Masyw Ślęży najlepiej wychodzą w kadrze jako las, ścieżka i światło między drzewami. Jesień daje kolory, wiosna — świeżą zieleń, zima — spokój (przy dobrym obuwiu).\n\nNie spodziewaj się alpejskiej panoramy z każdym krokiem — tu nagrodą jest rytm marszu i bliskość natury bez dalekiej podróży z Wrocławia.',
        },
      ],
      faq: [
        {
          question: 'Gdzie jest Radunia?',
          answer:
            'W Masywie Ślęży, w rejonie Sobótki (woj. dolnośląskie) — ok. 35–40 km na południowy zachód od Wrocławia.',
        },
        {
          question: 'Ile czasu zaplanować?',
          answer:
            'Zwykle 2–4 godziny, zależnie od wybranej pętli i tempa.',
        },
        {
          question: 'Czy to jest trudne?',
          answer:
            'Raczej średnie — zależy od długości pętli. Po deszczu bywa ślisko i błotniście.',
        },
        {
          question: 'Czy Radunia nadaje się na regularne wypady z Wrocławia?',
          answer:
            'Tak — to jeden z najlepszych „bliskich” celów górskich. Dojazd jest krótki, a warianty tras pozwalają wracać bez nudy.',
        },
        {
          question: 'Czy w weekendy bywa tłoczno?',
          answer:
            'Tak, szczególnie przy popularnych parkingach i na głównych szlakach. Wcześniejszy start albo dzień powszedni dają więcej spokoju.',
        },
      ],
    },
    highlights: [
      'Górski wypad blisko Wrocławia — bez dalekiego dojazdu',
      'Leśne trasy i pętle w Masywie Ślęży',
      'Dobry wybór na regularne treningi i spokojne wypady',
    ],
    practicalInfo: {
      visitDuration: '2–4 godziny (zależnie od wybranej pętli).',
      howToGetThere:
        'Dojazd w rejon Masywu Ślęży (okolice Sobótki) i start z wybranego parkingu; ok. 35–40 km od Wrocławia.',
      parking: 'Parkingi przy wejściach na szlaki (w weekendy mogą się zapełniać).',
      accessibility: 'Szlaki leśne; miejscami podejścia i błoto po opadach.',
      goodToKnow: [
        'Weź wodę i buty terenowe — minimum na każdą porę roku.',
        'Wiosną i jesienią klimat bywa najlepszy.',
        'Możesz łączyć Radunię z innymi szczytami Masywu Ślęży w dłuższy dzień.',
      ],
    },
    seo: {
      title: 'Radunia – Masyw Ślęży | trasy, pętle, wypad z Wrocławia',
      description:
        'Radunia w Masywie Ślęży: pomysł na górski wypad blisko Wrocławia. Warianty tras, czas przejścia i praktyczne wskazówki przed wyjściem na szlak.',
      keywords: [
        'Radunia',
        'Masyw Ślęży',
        'Sobótka',
        'szlak',
        'wypad z Wrocławia',
        'góry blisko Wrocławia',
        'Dolny Śląsk',
        'pętla Ślęża',
        'trekking',
      ],
    },
    locationLabel: 'Masyw Ślęży (okolice Sobótki)',
    lat: 50.837369,
    lng: 16.710386,
  },
]

export const PLACES_COUNT_MINI = placesMini.length

